"use client"

import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { AuthContext } from "../App"
import { useTheme } from "../contexts/ThemeContext"
import {
  Plus,
  Search,
  X,
  Eye,
  ShoppingCart,
  AlertTriangle,
  CheckCircle,
  Package,
  HelpCircle,
  MessageCircle,
  Trash2,
  Clock,
  UserCheck,
  Image as ImageIcon,
  ZoomIn,
  Filter,
  Stethoscope,
  FileText,
  Sparkles,
  Scissors,
  Star,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Copy,
  Syringe,
  HeartPulse,
  Briefcase,
  SprayCan,
  Shirt,
  Laptop
} from "lucide-react"
import "./NuevoPedido.css"
import {
  clienteUsaSubfamilia,
  categoriaProducto,
  compatibilidadAlAnadirProducto,
  tipoDePedidoSegunProductos,
} from "../utils/subfamiliaPedido"

const API_URL = process.env.REACT_APP_API_URL;


// Hook personalizado para debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Replica del parser del backend (parseUnidadBaseFactor en routes/Stock.js):
// "1 CAJA DE 50" -> 50, "1 BOLSA DE 100" -> 100, "1 unidad" -> 1, "" -> 1
const parseUnidadBaseFactor = (unidadbasica) => {
  if (!unidadbasica) return 1
  const matches = String(unidadbasica).match(/\d+/g)
  if (!matches || matches.length < 2) return 1
  const factor = parseInt(matches[matches.length - 1], 10)
  return (isNaN(factor) || factor < 1) ? 1 : factor
}

// Funciones para manejar sessionStorage de líneas temporales
const getSessionKey = (pedidoId) => `pedido_temporal_${pedidoId}`

const getLineasTemporales = (pedidoId) => {
  if (!pedidoId) return {}
  try {
    const data = sessionStorage.getItem(getSessionKey(pedidoId))
    return data ? JSON.parse(data) : {}
  } catch {
    return {}
  }
}

const setLineaTemporal = (pedidoId, productId, lineaData) => {
  if (!pedidoId) return
  try {
    const lineasTemporales = getLineasTemporales(pedidoId)
    // Mantenemos la entrada si: cantidad > 0, o es un pendiente de stock bajo
    // (cantidad 0 esperada, el user todavia no ha decidido cantidad).
    if (lineaData.cantidad > 0 || lineaData.pendienteStock) {
      lineasTemporales[productId] = lineaData
    } else {
      delete lineasTemporales[productId]
    }
    sessionStorage.setItem(getSessionKey(pedidoId), JSON.stringify(lineasTemporales))
  } catch (error) {
    console.error("Error guardando en sessionStorage:", error)
  }
}

const removeLineaTemporal = (pedidoId, productId) => {
  if (!pedidoId) return
  try {
    const lineasTemporales = getLineasTemporales(pedidoId)
    delete lineasTemporales[productId]
    sessionStorage.setItem(getSessionKey(pedidoId), JSON.stringify(lineasTemporales))
  } catch (error) {
    console.error("Error removiendo de sessionStorage:", error)
  }
}

const clearLineasTemporales = (pedidoId) => {
  if (!pedidoId) return
  try {
    sessionStorage.removeItem(getSessionKey(pedidoId))
  } catch (error) {
    console.error("Error limpiando sessionStorage:", error)
  }
}

// Función para verificar si requiere aprobación
const requiereAprobacion = (clienteId, clinicaMatriz) => {
  if (clienteId == 999999 || clienteId == 131) {
    return false;
  }
  if (clinicaMatriz == 999999) {
    return true;
  }
  if (clinicaMatriz == 131) {
    return true;
  }
  return false;
};

// Verificar si requiere selector de tipo
const requiereTipoPedido = (clienteId, clinicaMatriz) => {
  console.log('🔍 Verificando si requiere tipo de pedido:', { clienteId, clinicaMatriz });
  if (clienteId == 999999 || clienteId == 131) {
    console.log('✅ Cliente es matriz (131 o 999999) - REQUIERE SELECTOR');
    return true;
  }
  if (clinicaMatriz == 999999 || clinicaMatriz == 131) {
    console.log('✅ Cliente derivado de matriz (131 o 999999) - REQUIERE SELECTOR');
    return true;
  }
  console.log('❌ Cliente NO requiere selector de tipo');
  return false;
};

const debeVerFiltroFamilia = (clienteId, clinicaMatriz) => {
  if (clienteId == 999999 || clienteId == 131) {
    return true;
  }
  if (clinicaMatriz == 999999 || clinicaMatriz == 131) {
    return true;
  }
  return false;
};

// Opciones del filtro de familia (dropdown con iconos lucide-react).
// El icono y el label se renderizan en el botón y en cada item.
const FAMILIA_FILTRO_OPTIONS = [
  { value: "todos",              label: "Todos los productos", Icon: Package },
  { value: "material-sanitario", label: "Material Sanitario",  Icon: Stethoscope },
  { value: "CIRUGIA",            label: "Cirugía",             Icon: Syringe },
  { value: "MEDICINA",           label: "Medicina",            Icon: HeartPulse },
  { value: "OFICINA",            label: "Oficina",             Icon: Briefcase },
  { value: "LIMPIEZA",           label: "Limpieza",            Icon: SprayCan },
  { value: "UNIFORMES",          label: "Uniformes",           Icon: Shirt },
  { value: "INFORMATICA",        label: "Informática",         Icon: Laptop },
]

// El selector de familia nunca ofrece "Todos los productos" ni "Material
// Sanitario": solo familias concretas (Cirugía / Medicina / Oficina /
// Limpieza / Uniformes / Informática).
const FAMILIA_FILTRO_OCULTAS = ["todos", "material-sanitario"]
const getFamiliaFiltroOptions = () =>
  FAMILIA_FILTRO_OPTIONS.filter(o => !FAMILIA_FILTRO_OCULTAS.includes(o.value))

// Normaliza familia para comparar: mayúsculas y SIN diacríticos. En BD se
// guarda 'INFORMATICA' (sin tilde), pero un dato sucio con tilde no debe
// romper la detección.
const normalizarFamilia = (familia) =>
  String(familia || '').toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Mapeo EXPLÍCITO: value del desplegable -> familias reales aceptadas (ya
// normalizadas, en mayúsculas y sin tilde). No comparamos el value en crudo
// contra `producto.familia` porque no siempre coinciden: el filtro
// "UNIFORMES" debe capturar los productos cuya familia real es 'VESTUARIO'
// (proveedor MELAN VESTUARI LABORAL). Se aceptan ambos alias por si el dato
// se normaliza en el futuro.
const FAMILIAS_ACEPTADAS_POR_FILTRO = {
  OFICINA:     ["OFICINA"],
  LIMPIEZA:    ["LIMPIEZA"],
  UNIFORMES:   ["VESTUARIO", "UNIFORMES"],
  INFORMATICA: ["INFORMATICA"],
};

// Función para clasificar familia de producto.
// Devuelve el value del filtro correspondiente, o "material-sanitario".
const clasificarFamilia = (familiaProducto) => {
  if (!familiaProducto) return "material-sanitario";
  const familia = normalizarFamilia(familiaProducto);
  for (const [valueFiltro, alias] of Object.entries(FAMILIAS_ACEPTADAS_POR_FILTRO)) {
    // Exacta, no `includes`: 'MATERIAL DE OFICINA' no debe caer en 'oficina'
    // (Medicina + Oficina está bloqueado y hoy esos productos son sanitarios).
    if (alias.includes(familia)) return valueFiltro;
  }
  return "material-sanitario";
};

// Colapsa las familias en las categorías madre de un pedido:
//   - "sanitario": material sanitario -> exige elegir Cirugía o Medicina
//   - "oficina_limpieza": Oficina + Limpieza -> tipo SIEMPRE Cirugía
//
// IMPORTANTE: Uniformes e Informática son COMPATIBLES CON TODO, así que NO
// se colapsan en "oficina_limpieza". Esa categoría es una regla de bloqueo:
// deshabilita el botón "Medicina Estética" y fuerza el tipo a Cirugía. Meter
// ahí Uniformes/Informática impediría hacer un pedido de Medicina con
// uniformes, que es justo lo contrario de lo decidido. Se tratan como
// "sanitario" (neutro): no fuerzan tipo y dejan al usuario elegir.
const getCategoriaPedido = (familiaProducto) => {
  const clasificada = clasificarFamilia(familiaProducto);
  return (clasificada === "OFICINA" || clasificada === "LIMPIEZA")
    ? "oficina_limpieza"
    : "sanitario";
};

const nombreCategoriaPedido = (categoria) =>
  categoria === "sanitario" ? "Material Sanitario" : "Oficina y Limpieza";

// Etiqueta legible del tipo de producto para la columna "Tipo".
// La subfamilia (Cirugía / Medicina) tiene prioridad sobre la familia.
const etiquetaTipoProducto = (producto) => {
  // Aceptamos tanto el producto completo como solo la familia (compat).
  const sub = typeof producto === 'object' && producto !== null
    ? String(producto.subfamilia || '').toLowerCase()
    : '';
  if (sub === 'cirugia')  return 'Cirugía';
  if (sub === 'medicina') return 'Medicina';
  const familia = typeof producto === 'object' && producto !== null
    ? producto.familia
    : producto;
  switch (clasificarFamilia(familia)) {
    case "OFICINA": return "Oficina";
    case "LIMPIEZA": return "Limpieza";
    case "UNIFORMES": return "Uniformes";
    case "INFORMATICA": return "Informática";
    default: return "Sanitario";
  }
};

// Componente para mostrar referencia truncada con tooltip
const TruncatedReference = React.memo(({ referencia, maxLength = 10 }) => {
  const [showTooltip, setShowTooltip] = useState(false)

  if (!referencia || referencia.length <= maxLength) {
    return <span>{referencia || "-"}</span>
  }

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span className="cursor-help">
        {referencia.substring(0, maxLength)}...
      </span>
      {showTooltip && (
        <div className="absolute z-50 bg-gray-900 text-white text-xs rounded py-1 px-2 -top-8 left-0 whitespace-nowrap shadow-lg">
          {referencia}
          <div className="absolute top-full left-2 w-0 h-0 border-l-2 border-l-transparent border-r-2 border-r-transparent border-t-2 border-t-gray-900"></div>
        </div>
      )}
    </div>
  )
})

// Componente para mostrar imagen del producto
const ProductImage = React.memo(({ imagen, nombre, onImageClick }) => {
  const [imageError, setImageError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  if (!imagen || imageError) {
    return null
  }

  return (
    <div className="w-10 h-10 relative">
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded flex items-center justify-center">
          <ImageIcon size={14} className="text-gray-400" />
        </div>
      )}
      <img
        src={imagen}
        alt={nombre}
        className={`w-full h-full object-cover rounded cursor-pointer hover:opacity-80 transition-opacity ${
          isLoading ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setImageError(true)
          setIsLoading(false)
        }}
        onClick={() => onImageClick(imagen, nombre)}
        title="Click para ver en grande"
      />
    </div>
  )
})

// Modal para mostrar imagen en grande
const ImageModal = React.memo(({ isOpen, imageSrc, imageAlt, onClose }) => {
  const modalRef = useRef(null)

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose()
    }
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.addEventListener('mousedown', handleClickOutside)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('mousedown', handleClickOutside)
      document.body.style.overflow = 'auto'
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="relative max-w-4xl max-h-full bg-white rounded-lg shadow-xl overflow-hidden"
      >
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={onClose}
            className="bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-4">
          <img
            src={imageSrc}
            alt={imageAlt}
            className="max-w-full max-h-[80vh] object-contain mx-auto rounded"
            onError={(e) => { e.target.style.display = 'none' }}
          />
          <div className="mt-4 text-center">
            <h3 className="text-lg font-medium text-gray-800">{imageAlt}</h3>
          </div>
        </div>
      </div>
    </div>
  )
})

// Componente memoizado para filas de productos
// Estrella de favorito con animación divertida al marcar (pop + estallido).
const FavStar = ({ esFavorito, onToggle, size = 14 }) => {
  const [burst, setBurst] = useState(false)
  const handleClick = useCallback((e) => {
    e.stopPropagation()
    if (!esFavorito) {
      setBurst(true)
      setTimeout(() => setBurst(false), 550)
    }
    onToggle && onToggle()
  }, [esFavorito, onToggle])
  return (
    <button
      type="button"
      onClick={handleClick}
      title={esFavorito ? "Quitar de favoritos" : "Añadir a favoritos"}
      className="fav-star-btn flex-shrink-0"
    >
      <Star
        size={size}
        fill={esFavorito ? "#f59e0b" : "none"}
        color={esFavorito ? "#f59e0b" : "#cbd5e1"}
        className={burst ? "fav-star-anim" : ""}
      />
      {burst && (
        <span className="fav-burst">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <span key={i} className="fav-spark" style={{ "--a": `${i * 60}deg` }} />
          ))}
        </span>
      )}
    </button>
  )
}

// Grupo sintético para mostrar los productos favoritos al principio del catálogo.
const FAVORITOS_FAMILIA = "★ Favoritos"

const ProductRow = React.memo(({
  producto,
  cantidadMostrada,
  onCantidadChange,
  onSolicitarProducto,
  onPrecioChange,
  onImageClick,
  userPermisos,
  mostrarColumnaImagen,
  mostrarColumnaTipo,
  esFavorito,
  onToggleFavorito
}) => {
  const [cantidadLocal, setCantidadLocal] = useState(cantidadMostrada || "")
  const [cantidadAnterior, setCantidadAnterior] = useState(cantidadMostrada || "")
  const lineaExistente = cantidadMostrada > 0

  React.useEffect(() => {
    const cantidadStr = cantidadMostrada ? cantidadMostrada.toString() : ""
    setCantidadLocal(cantidadStr)
    setCantidadAnterior(cantidadStr)
  }, [cantidadMostrada])

  const handleCantidadInputChange = useCallback((e) => {
    setCantidadLocal(e.target.value)
  }, [])

  const handleCantidadSubmit = useCallback(() => {
    const cantidadActual = cantidadLocal !== null && cantidadLocal !== undefined ?
      cantidadLocal.toString().trim() : ""
    const cantidadAnteriorStr = cantidadAnterior !== null && cantidadAnterior !== undefined ?
      cantidadAnterior.toString().trim() : ""

    if (cantidadActual === cantidadAnteriorStr) return

    console.log(`ProductRow: Cantidad cambió de "${cantidadAnteriorStr}" a "${cantidadActual}"`)

    let cantidadNum = 0
    if (cantidadActual && cantidadActual !== "") {
      try {
        const cantidadNormalizada = cantidadActual.replace(',', '.')
        cantidadNum = parseFloat(cantidadNormalizada)
        if (isNaN(cantidadNum)) cantidadNum = 0
        console.log(`ProductRow: "${cantidadActual}" parseado como ${cantidadNum}`)
      } catch (error) {
        console.error("Error al parsear cantidad en ProductRow:", error)
        cantidadNum = 0
      }
    }

    if (!cantidadActual || cantidadNum <= 0) {
      const cantidadAnteriorNum = parseFloat(cantidadAnteriorStr.replace(',', '.')) || 0
      if (cantidadAnteriorNum > 0) {
        console.log(`ProductRow: Eliminando producto (cantidad ${cantidadAnteriorNum} -> ${cantidadNum})`)
        onCantidadChange(producto.id, cantidadActual)
        setCantidadAnterior(cantidadActual)
      }
      return
    }

    if (cantidadNum > 0) {
      console.log(`ProductRow: Guardando cantidad ${cantidadNum} para producto ${producto.id}`)
      onCantidadChange(producto.id, cantidadActual)
      setCantidadAnterior(cantidadActual)
    }
  }, [producto.id, cantidadLocal, cantidadAnterior, onCantidadChange])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleCantidadSubmit()
  }, [handleCantidadSubmit])

  const handleSolicitarProductoLocal = useCallback(() => {
    onSolicitarProducto(producto.ref_proveedor || producto.raiz, producto.nombre)
  }, [producto.ref_proveedor, producto.raiz, producto.nombre, onSolicitarProducto])

  const handlePrecioChangeLocal = useCallback((e) => {
    onPrecioChange(producto.idprecio, e.target.value)
  }, [producto.idprecio, onPrecioChange])

  return (
    <tr className="hover:bg-gray-50 transition-colors duration-100">
      {mostrarColumnaImagen && (
        <td className="px-4 py-2.5">
          <ProductImage
            imagen={producto.imagen}
            nombre={producto.nombre}
            onImageClick={onImageClick}
          />
        </td>
      )}
      <td className="px-4 py-2.5 text-xs text-gray-500 font-mono">
        <TruncatedReference
          referencia={producto.ref_proveedor || producto.raiz}
          maxLength={12}
        />
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <FavStar
            esFavorito={esFavorito}
            onToggle={() => onToggleFavorito && onToggleFavorito(producto.id)}
            size={14}
          />
          <span className="text-xs font-medium text-gray-800">{producto.nombre}</span>
          {producto.unidadbasica && (
            <span className="text-xs text-gray-400 ml-1">({producto.unidadbasica})</span>
          )}
        </div>
      </td>
      <td className="px-4 py-2.5 text-xs text-gray-500">{producto.marca || "-"}</td>
      {mostrarColumnaTipo && (
        <td className="px-4 py-2.5 text-xs text-gray-500">
          {etiquetaTipoProducto(producto)}
        </td>
      )}
      <td className="px-4 py-2.5 text-right">
        {userPermisos < 3 ? (
          <input
            className="w-16 px-2 py-1 text-right border border-gray-300 rounded text-xs focus:outline-none focus:ring-1"
            type="text"
            defaultValue={producto.precio.toString()}
            onBlur={handlePrecioChangeLocal}
          />
        ) : (
          <span className="text-xs font-medium text-gray-700">
            {new Intl.NumberFormat("es-ES", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 3,
            }).format(producto.precio)} €
          </span>
        )}
      </td>
      <td className="px-4 py-2.5 text-center">
        {producto.cantidad_embalaje ? (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
            {producto.cantidad_embalaje}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
      <td className="px-4 py-2.5 text-right">
        {producto.precio !== 0 || userPermisos === 1 ? (
          <input
            className={`w-16 px-2 py-1 text-right border rounded text-xs focus:outline-none focus:ring-1 transition-colors ${
              lineaExistente
                ? "border-green-400 bg-green-50 text-green-800 font-medium"
                : "border-gray-300 hover:border-gray-400"
            }`}
            type="text"
            value={cantidadLocal}
            onChange={handleCantidadInputChange}
            onBlur={handleCantidadSubmit}
            onKeyDown={handleKeyDown}
            placeholder="0"
          />
        ) : (
          <button
            className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 transition-colors text-xs font-medium"
            onClick={handleSolicitarProductoLocal}
          >
            Solicitar
          </button>
        )}
      </td>
    </tr>
  )
})

// Componente para líneas del pedido (combinando BD + temporales)
const PedidoLineRow = React.memo(({
  linea,
  onCantidadChange,
  onEliminarLinea,
  esFavorito,
  onToggleFavorito
}) => {
  const [cantidadLocal, setCantidadLocal] = useState(linea.cantidad || "")

  React.useEffect(() => {
    setCantidadLocal(linea.cantidad || "")
  }, [linea.cantidad])

  const handleCantidadInputChange = useCallback((e) => {
    setCantidadLocal(e.target.value)
  }, [])

  const handleCantidadSubmit = useCallback(() => {
    onCantidadChange(linea.idarticulo, cantidadLocal)
  }, [linea.idarticulo, cantidadLocal, onCantidadChange])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleCantidadSubmit()
  }, [handleCantidadSubmit])

  const handleEliminarLineaLocal = useCallback(() => {
    onEliminarLinea(linea)
  }, [linea, onEliminarLinea])

  const esPendienteStock = !!linea.pendienteStock
  return (
    <tr className="hover:bg-gray-50 transition-colors duration-100">
      <td className="px-4 py-2.5">
        <div className="text-xs font-medium text-gray-800 leading-tight flex items-start gap-1.5">
          <span className="flex-shrink-0 mt-0.5 inline-flex">
            <FavStar
              esFavorito={esFavorito}
              onToggle={() => onToggleFavorito && onToggleFavorito(linea.idarticulo)}
              size={13}
            />
          </span>
          <span>
            {linea.nombre}
            {linea.unidadbasica && (
              <span className="text-gray-400 font-normal"> ({linea.unidadbasica})</span>
            )}
          </span>
        </div>
        <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
          <TruncatedReference referencia={linea.ref_proveedor || linea.raiz || "-"} maxLength={15} />
          {esPendienteStock && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px]"
              title="Producto añadido automáticamente al estar bajo stock. Introduce la cantidad a comprar."
            >
              <span style={{ color: '#92400e' }}>·</span>
              <span className="px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                actual: {linea.stockActual} ud.
              </span>
              <span className="px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: '#fde68a', color: '#78350f' }}>
                mín: {linea.stockMinimo} ud.
              </span>
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right whitespace-nowrap">
        <div className="text-xs font-medium text-gray-700">
          {new Intl.NumberFormat("es-ES", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(linea.precio)}
        </div>
        <div className="text-xs text-gray-400">{linea.tipo_iva || linea.iva || 21}% IVA</div>
      </td>
      <td className="px-3 py-2.5 text-right">
        <input
          className="w-24 px-2 py-1.5 text-right border border-gray-300 rounded text-sm font-medium focus:outline-none focus:ring-1"
          type="text"
          value={cantidadLocal}
          onChange={handleCantidadInputChange}
          onBlur={handleCantidadSubmit}
          onKeyDown={handleKeyDown}
        />
        {linea.cantidad_embalaje && (
          <div className="text-xs text-gray-400 text-right mt-0.5">E:{linea.cantidad_embalaje}</div>
        )}
      </td>
      <td className="px-3 py-2.5 text-right">
        <span className="text-xs font-semibold text-gray-800">
          {new Intl.NumberFormat("es-ES", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(linea.subtotal)}
        </span>
      </td>
      <td className="px-2 py-2.5 text-center">
        <button
          onClick={handleEliminarLineaLocal}
          className="text-gray-300 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors"
          title="Eliminar producto del pedido"
        >
          <Trash2 size={13} />
        </button>
      </td>
    </tr>
  )
})

const NuevoPedido = () => {
  const { user } = useContext(AuthContext)
  const navigate = useNavigate()
  const modalRef = useRef(null)
  const comentariosModalRef = useRef(null)

  // ─── TEMA DINÁMICO ───────────────────────────────────────────────────
  const { theme } = useTheme()
  const bgPrimary = { backgroundColor: theme.primary }
  const bgPrimaryLight = { backgroundColor: theme.primaryLight }
  const borderPrimary = { borderColor: theme.primary }
  const textPrimary = { color: theme.primary }
  const textPrimaryDark = { color: theme.primaryDark }
  // ─────────────────────────────────────────────────────────────────────

  const getClienteActivo = () => {
    try {
      const clienteData = sessionStorage.getItem('activeClient')
      return clienteData ? JSON.parse(clienteData) : null
    } catch (error) {
      console.error("Error obteniendo datos del cliente:", error)
      return null
    }
  }

  const clienteActivo = getClienteActivo()
  const nombreClienteActivo = clienteActivo?.razon_social || "Cliente no identificado"

  // Estados para cliente y pedido
  const [cliente, setCliente] = useState(null)
  const [pedidoId, setPedidoId] = useState(null)
  const [npedido, setNpedido] = useState("S/N")

  // Estados para proveedores
  const [proveedores, setProveedores] = useState([])
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null)

  // Estados para búsqueda de productos
  const [searchQuery, setSearchQuery] = useState("")
  const [allProducts, setAllProducts] = useState([])
  const [filteredProducts, setFilteredProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)

  // Estados para líneas de pedido (SOLO de la BD)
  const [lineasPedidoBD, setLineasPedidoBD] = useState([])
  const [totalPedidoBD, setTotalPedidoBD] = useState(0)

  // Estado para líneas temporales
  const [lineasTemporales, setLineasTemporales] = useState({})

  // Gestion de stock: ids de productos cuya cantidad < minimo en este cliente.
  // Si gestion_stock no esta activo, este Set queda vacio y nada se dispara.
  const [stockBajoMinimoSet, setStockBajoMinimoSet] = useState(new Set())
  const [stockEmbalajes, setStockEmbalajes] = useState({}) // { productoId: cantidad_embalaje }
  // Productos bajo minimo SIN embalaje: el user debe meter la cantidad a mano
  const [stockBajoMinSinEmbalaje, setStockBajoMinSinEmbalaje] = useState([])
  // Guard contra re-ejecucion del bajo-minimo en el mismo (cliente,proveedor,pedido)
  const reposicionStockExecutedRef = useRef({})
  // Refs para leer estado actual SIN ponerlo en deps del callback (rompe el ciclo)
  const lineasPedidoBDRef = useRef([])
  const lineasTemporalesRef = useRef({})

  // Estados para comentarios
  const [comentarios, setComentarios] = useState([])
  const [nuevoComentario, setNuevoComentario] = useState("")
  const [cantidadComentario, setCantidadComentario] = useState("")
  const [precioComentario, setPrecioComentario] = useState("")
  const [comentariosModalVisible, setComentariosModalVisible] = useState(false)

  // Estados para finalizar
  const [npedidoCliente, setNpedidoCliente] = useState("")
  const [modalVisible, setModalVisible] = useState(false)

  // Estado para mensajes y errores
  const [mensaje, setMensaje] = useState({ texto: "", tipo: "" })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [loadingCambioProveedor, setLoadingCambioProveedor] = useState(false)

  const [pasoActual, setPasoActual] = useState(1)
  const [detalleVisible, setDetalleVisible] = useState(true)
  const [tooltipVisible, setTooltipVisible] = useState("")

  // Estados para modal de imagen
  const [modalImagenVisible, setModalImagenVisible] = useState(false)
  const [imagenSeleccionada, setImagenSeleccionada] = useState({ src: "", alt: "" })

  const updateTimeoutRef = useRef()
  const saveQueueRef = useRef(new Set())

  const [mostrarFiltroFamilia, setMostrarFiltroFamilia] = useState(false)
  const [familiaFiltro, setFamiliaFiltro] = useState("todos")
  const [familiaFiltroOpen, setFamiliaFiltroOpen] = useState(false)
  const familiaFiltroRef = useRef(null)

  const debouncedSearchQuery = useDebounce(searchQuery, 300)
  const token = useMemo(() => localStorage.getItem("token"), [])

  const [necesitaAprobacion, setNecesitaAprobacion] = useState(false)

  const [tipoPedido, setTipoPedido] = useState('')
  const [mostrarSelectorTipo, setMostrarSelectorTipo] = useState(false)

  // Estados para repetición de pedidos
  const [tabIzquierda, setTabIzquierda] = useState('catalogo') // 'catalogo' | 'repetir' | 'favoritos'
  const [tabRepetir, setTabRepetir] = useState('anteriores') // 'anteriores' | 'favoritos'
  const [verFavoritosPrimero, setVerFavoritosPrimero] = useState(false)
  const [pedidosAnteriores, setPedidosAnteriores] = useState([])
  const [pedidosFavoritos, setPedidosFavoritos] = useState([])
  const [expandedPedidos, setExpandedPedidos] = useState(new Set())
  const [loadingRepetir, setLoadingRepetir] = useState(false)
  const [loadingCopiar, setLoadingCopiar] = useState(false)

  // Productos favoritos de la CLÍNICA (set de producto_id). Se usa para pintar
  // la estrella (en catálogo y en las líneas del pedido) y para el apartado
  // "Favoritos".
  const [favoritosIds, setFavoritosIds] = useState(new Set())

  // Cargar líneas temporales cuando cambia el pedidoId.
  // OJO: si ya hay pendientes en state (puestas por cargarYReponer), las preservamos
  // aunque sessionStorage no las tenga aun (race condition).
  useEffect(() => {
    if (pedidoId) {
      const temporales = getLineasTemporales(pedidoId)
      console.log('[useEffect-pedidoId] reading sessionStorage:', temporales)
      setLineasTemporales(prev => {
        const merged = { ...temporales }
        Object.entries(prev).forEach(([id, l]) => {
          if (l && l.pendienteStock && !merged[id]) {
            console.log('[useEffect-pedidoId] preservando pendiente del state:', id)
            merged[id] = l
          }
        })
        return merged
      })
    }
  }, [pedidoId])

  // Mantener refs sincronizados (para usarlos en callbacks sin meterlos en deps)
  useEffect(() => { lineasPedidoBDRef.current  = lineasPedidoBD;  }, [lineasPedidoBD])
  useEffect(() => { lineasTemporalesRef.current = lineasTemporales; }, [lineasTemporales])

  useEffect(() => {
    if (cliente) {
      const debeVer = debeVerFiltroFamilia(cliente.id, cliente.clinica_matriz)
      setMostrarFiltroFamilia(debeVer)
      console.log("Debe ver filtro de familia:", debeVer)
      const requiereTipo = requiereTipoPedido(cliente.id, cliente.clinica_matriz)
      setMostrarSelectorTipo(requiereTipo)
      console.log("Debe ver selector de tipo:", requiereTipo)
    }
  }, [cliente])

  const handleImageClick = useCallback((imageSrc, imageAlt) => {
    setImagenSeleccionada({ src: imageSrc, alt: imageAlt })
    setModalImagenVisible(true)
  }, [])

  const handleCloseImageModal = useCallback(() => {
    setModalImagenVisible(false)
    setImagenSeleccionada({ src: "", alt: "" })
  }, [])

  const hayProductosConImagen = useMemo(() => {
    return filteredProducts.some(producto => producto.imagen && producto.imagen.trim() !== '')
  }, [filteredProducts])

  const makeRequest = useCallback(async (url, options = {}) => {
    const defaultOptions = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    }
    const response = await fetch(url, defaultOptions)
    if (!response.ok) {
      // Intentamos leer el body para devolverlo al caller (mensajes de validación).
      let body = null
      try { body = await response.json() } catch {}
      const err = new Error(`Request failed: ${response.status}`)
      err.status = response.status
      err.body = body
      throw err
    }
    return response.json()
  }, [token])

  const recargarLineasPedido = useCallback(async () => {
    if (!pedidoId) return
    try {
      const data = await makeRequest(`${API_URL}/api/pedidos/${pedidoId}/lineas`)
      if (data && data.lineas) {
        setLineasPedidoBD(data.lineas)
        setTotalPedidoBD(data.total || 0)
        setNpedido(data.npedido || "S/N")
        // Si el pedido tenia el numero corrupto ("S/N cirugia"...), no lo
        // recargamos tal cual en el campo: lo normalizamos a "S/N" limpio.
        setNpedidoCliente(/^s\/?n/i.test(String(data.npedido || "")) ? "S/N" : (data.npedido || "S/N"))
        if (data.lineas.length > 0) {
          setPasoActual((prev) => Math.max(prev, 3))
        }
      }
    } catch (error) {
      console.error("Error al cargar líneas de pedido:", error)
      if (error.message.includes('404')) {
        setLineasPedidoBD([])
        setTotalPedidoBD(0)
      }
    }
  }, [pedidoId, makeRequest])

  const cargarTodosLosProductos = useCallback(async (proveedorId) => {
    if (!proveedorId || !cliente) return
    try {
      setLoadingProducts(true)
      const data = await makeRequest(
        `${API_URL}/api/productos/buscar?query=&proveedor_id=${proveedorId}&cliente_id=${cliente.id}&limit=5000`
      )
      setAllProducts(data.productos || [])
      setFilteredProducts(data.productos || [])
    } catch (error) {
      console.error("Error al cargar productos:", error)
      setMensaje({ texto: "Error al cargar productos del proveedor", tipo: "error" })
    } finally {
      setLoadingProducts(false)
    }
  }, [cliente, makeRequest])

  /**
   * Si el cliente tiene gestion de stock, consultamos los productos bajo minimo
   * para el proveedor seleccionado y los auto-anadimos al pedido:
   *   - cantidad = embalaje si el producto tiene cantidad_embalaje > 0
   *   - cantidad = 1 si no tiene embalaje
   * Solo se auto-anade si el producto NO esta ya en el pedido.
   */
  const cargarYReponerStockBajoMinimo = useCallback(async (proveedorId) => {
    console.log('[stock-reposicion] llamada', { proveedorId, clienteId: cliente?.id, pedidoId })
    if (!proveedorId || !cliente?.id || !pedidoId) {
      console.log('[stock-reposicion] skip: falta proveedor, cliente o pedidoId')
      return
    }
    // Guard: solo correr una vez por combinacion (cliente, proveedor, pedido).
    // EXCEPCION: si hay pendientes en sessionStorage sin metadata (unidadbasica),
    // forzamos refresh para arreglar el calculo de factor.
    const key = `${cliente.id}-${proveedorId}-${pedidoId}`
    const tempActualesPreCheck = lineasTemporalesRef.current
    const hayPendienteIncompleta = Object.values(tempActualesPreCheck).some(
      l => l?.pendienteStock && (!l.unidadbasica || !l.factorUnidadBase || l.factorUnidadBase < 1)
    )
    if (reposicionStockExecutedRef.current[key] && !hayPendienteIncompleta) {
      console.log('[stock-reposicion] skip: ya ejecutado para', key)
      return
    }
    if (hayPendienteIncompleta) {
      console.log('[stock-reposicion] forzando refresh por metadata incompleta')
    }
    reposicionStockExecutedRef.current[key] = true
    console.log('[stock-reposicion] EJECUTANDO para', key)

    try {
      const data = await makeRequest(
        `${API_URL}/api/stock/cliente/${cliente.id}/bajo-minimo?proveedor_id=${proveedorId}`
      )
      // Dedupe por producto_id por si el backend devuelve duplicados
      const productosRaw = data?.productos || []
      const productos = Array.from(new Map(productosRaw.map(p => [p.producto_id, p])).values())

      const nuevoSet = new Set(productos.map(p => p.producto_id))
      const embalajes = {}
      productos.forEach(p => { embalajes[p.producto_id] = p.cantidad_embalaje })
      setStockBajoMinimoSet(nuevoSet)
      setStockEmbalajes(embalajes)
      setStockBajoMinSinEmbalaje([])

      // AUTO-RELLENO DE STOCK BAJO DESACTIVADO.
      // Antes se auto-anadian al pedido TODOS los productos bajo minimo como
      // "pendientes" (ej. Castellana: 185 lineas cada vez que se iba a pedir,
      // habia que borrarlas a mano). Mantenemos stockBajoMinimoSet para el
      // resaltado en el catalogo, pero NO inyectamos pendientes al pedido y
      // limpiamos cualquiera que hubiera quedado en sesion.
      // Para reactivarlo, poner AUTO_REPONER_STOCK_BAJO = true.
      const AUTO_REPONER_STOCK_BAJO = false
      if (!AUTO_REPONER_STOCK_BAJO) {
        const tempActuales = lineasTemporalesRef.current
        const pendientesALimpiar = Object.entries(tempActuales)
          .filter(([, l]) => l?.pendienteStock)
          .map(([id]) => parseInt(id))
        if (pendientesALimpiar.length > 0) {
          pendientesALimpiar.forEach(id => removeLineaTemporal(pedidoId, id))
          setLineasTemporales(prev => {
            const next = { ...prev }
            pendientesALimpiar.forEach(id => { delete next[id] })
            return next
          })
        }
        return
      }

      if (productos.length === 0) return

      // Leemos los lineas actuales por ref (no via deps)
      const lineasBDActuales   = lineasPedidoBDRef.current
      const lineasTempActuales = lineasTemporalesRef.current

      // IDs de productos bajo minimo en ESTE proveedor
      const idsActuales = new Set(productos.map(p => p.producto_id))

      // PASO 1: limpiar pendientes viejas que NO sean del proveedor actual
      // (productos que estaban pendientes de otro proveedor seleccionado antes).
      const aLimpiar = []
      Object.entries(lineasTempActuales).forEach(([id, l]) => {
        if (l?.pendienteStock && !idsActuales.has(parseInt(id))) {
          aLimpiar.push(parseInt(id))
        }
      })
      if (aLimpiar.length > 0) {
        console.log('[stock-reposicion] limpiando pendientes de otro proveedor:', aLimpiar)
        aLimpiar.forEach(id => removeLineaTemporal(pedidoId, id))
        setLineasTemporales(prev => {
          const next = { ...prev }
          aLimpiar.forEach(id => { delete next[id] })
          return next
        })
      }

      // PASO 2: añadir / refrescar pendientes del proveedor actual
      let pendientesAdded = 0
      for (const p of productos) {
        const yaEnBD     = lineasBDActuales.some(l => l.idarticulo === p.producto_id && l.cantidad > 0)
        const tempActual = lineasTempActuales[p.producto_id]
        const yaConCantidadReal = !!tempActual && !tempActual.eliminada && tempActual.cantidad > 0 && !tempActual.pendienteStock
        const userMarcoBorrada  = !!tempActual && tempActual.eliminada

        if (yaEnBD || yaConCantidadReal || userMarcoBorrada) {
          console.log(`[stock-reposicion] skip ${p.producto_id}:`, { yaEnBD, yaConCantidadReal, userMarcoBorrada })
          continue
        }

        // factor robusto: backend -> parser de unidadbasica -> cantidad_embalaje -> 1
        let factor = parseInt(p.factor_unidad_base, 10)
        if (!Number.isFinite(factor) || factor < 1) factor = parseUnidadBaseFactor(p.unidadbasica)
        if (!Number.isFinite(factor) || factor < 1) {
          const cEmb = parseInt(p.cantidad_embalaje, 10)
          if (Number.isFinite(cEmb) && cEmb > 0) factor = cEmb
        }
        if (!Number.isFinite(factor) || factor < 1) factor = 1

        const lineaPendienteBase = {
          id: `temp_pendiente_${p.producto_id}`,
          idarticulo: p.producto_id,
          nombre: p.nombre,
          ref_proveedor: p.ref_proveedor,
          precio: parseFloat(p.precio_sin_iva) || 0,
          iva: p.tipo_iva || 21,
          tipo_iva: p.tipo_iva || 21,
          cantidad_embalaje: p.cantidad_embalaje,
          unidadbasica: p.unidadbasica || '',
          factorUnidadBase: factor,
          temporal: true,
          pendienteStock: true,
          stockActual: p.cantidad,
          stockMinimo: p.minimo,
        }

        // Si ya hay una pendiente previa, refrescamos su metadata pero preservamos
        // la cantidad/subtotal que el user haya tipeado.
        let lineaPendiente
        if (tempActual && tempActual.pendienteStock) {
          lineaPendiente = {
            ...lineaPendienteBase,
            cantidad: tempActual.cantidad || 0,
            subtotal: tempActual.subtotal || 0,
          }
          console.log('[stock-reposicion] refrescando metadata pendiente:', p.producto_id, 'factor', factor)
        } else {
          lineaPendiente = { ...lineaPendienteBase, cantidad: 0, subtotal: 0 }
          console.log('[stock-reposicion] añadiendo pendiente:', p.producto_id, p.nombre, 'factor', factor)
          pendientesAdded++
        }
        setLineaTemporal(pedidoId, p.producto_id, lineaPendiente)
        setLineasTemporales(prev => ({ ...prev, [p.producto_id]: lineaPendiente }))
      }

      console.log(`[stock-reposicion] FIN. ${pendientesAdded} pendientes añadidos de ${productos.length} bajo minimo`)
      if (pendientesAdded > 0) {
        setMensaje({
          texto: `${pendientesAdded} producto(s) bajo mínimo añadidos como pendientes. Introduce la cantidad manualmente.`,
          tipo: 'success'
        })
        setTimeout(() => setMensaje({ texto: '', tipo: '' }), 7000)
      }
    } catch (error) {
      console.error('Error consultando stock bajo minimo:', error)
    }
  }, [cliente, pedidoId, makeRequest])

  // Categoría madre del pedido actual (sanitario | oficina_limpieza | null).
  // Se infiere de la primera línea activa cuya familia conocemos por el catálogo.
  // Como el pedido es de un único proveedor, allProducts es la fuente fiable.
  // Vale null cuando el pedido está vacío -> el catálogo vuelve a mostrarse entero.
  const categoriaPedidoActual = useMemo(() => {
    const idsActivos = []
    lineasPedidoBD.forEach(l => {
      const t = lineasTemporales[l.idarticulo]
      if (t && t.eliminada) return
      const cant = (t && !t.pendienteStock) ? t.cantidad : l.cantidad
      if (cant > 0) idsActivos.push(l.idarticulo)
    })
    Object.values(lineasTemporales).forEach(t => {
      if (t.eliminada) return
      if (lineasPedidoBD.some(l => l.idarticulo === t.idarticulo)) return
      if (t.pendienteStock || t.cantidad > 0) idsActivos.push(t.idarticulo)
    })
    for (const id of idsActivos) {
      const prod = allProducts.find(p => p.id === id)
      if (prod) return getCategoriaPedido(prod.familia)
    }
    return null
  }, [lineasPedidoBD, lineasTemporales, allProducts])

  // El tipo de pedido se auto-deriva (y el selector se oculta) cuando hay
  // algún indicador que desambigüe el tipo:
  //   - Producto con subfamilia 'cirugia' o 'medicina'.
  //   - Producto de Limpieza u Oficina (Medicina+Limpieza/Oficina está
  //     prohibido, así que la presencia de limpieza/oficina fuerza Cirugía).
  // Solo se muestra el selector cuando TODOS los productos activos son
  // sanitario genérico (sin subfamilia y sin limpieza/oficina).
  //
  // Uniformes e Informática NO cuentan como autoderivación a propósito: son
  // compatibles con Cirugía y con Medicina, así que no desambiguan nada. Si se
  // incluyeran aquí, un pedido solo de uniformes ocultaría el selector sin que
  // nadie fijase el tipo (getCategoriaPedido no los marca como
  // "oficina_limpieza") y el pedido quedaría sin tipo. Dejándolos fuera, el
  // selector sigue visible y el usuario elige.
  const tipoPedidoEsAutoderivado = useMemo(() => {
    const idsActivos = []
    lineasPedidoBD.forEach(l => {
      const t = lineasTemporales[l.idarticulo]
      if (t && t.eliminada) return
      const cant = (t && !t.pendienteStock) ? t.cantidad : l.cantidad
      if (cant > 0) idsActivos.push(l.idarticulo)
    })
    Object.values(lineasTemporales).forEach(t => {
      if (t.eliminada) return
      if (lineasPedidoBD.some(l => l.idarticulo === t.idarticulo)) return
      if ((t.cantidad > 0 || t.pendienteStock) && !t.pendienteStock) idsActivos.push(t.idarticulo)
    })
    return idsActivos.some(id => {
      const prod = allProducts.find(p => p.id === id)
      const cat = categoriaProducto(prod)
      return cat === 'cirugia' || cat === 'medicina'
          || cat === 'limpieza' || cat === 'oficina'
    })
  }, [lineasPedidoBD, lineasTemporales, allProducts])

  useEffect(() => {
    if (!debouncedSearchQuery.trim() && familiaFiltro === "todos") {
      setFilteredProducts(allProducts)
    } else {
      const query = debouncedSearchQuery.toLowerCase()
      let filtered = allProducts
      if (query) {
        filtered = filtered.filter(producto =>
          producto.nombre.toLowerCase().includes(query) ||
          (producto.ref_proveedor && producto.ref_proveedor.toLowerCase().includes(query)) ||
          (producto.raiz && producto.raiz.toLowerCase().includes(query)) ||
          (producto.marca && producto.marca.toLowerCase().includes(query))
        )
      }
      if (familiaFiltro !== "todos") {
        filtered = filtered.filter(producto => {
          // Filtros por subfamilia (solo aplican a productos con subfamilia
          // asignada: cls 131/999999).
          const sub = String(producto.subfamilia || '').toLowerCase()
          const familiaClasificada = clasificarFamilia(producto.familia)
          // "Material sanitario genérico" = sanitario sin subfamilia específica.
          const esSanitarioGenerico = familiaClasificada === "material-sanitario" && !sub
          // Al filtrar por Cirugía o Medicina mostramos TAMBIÉN el material
          // sanitario genérico (válido para ambos casos), además de los productos
          // de la subfamilia seleccionada.
          if (familiaFiltro === "CIRUGIA")  return sub === "cirugia"  || esSanitarioGenerico
          if (familiaFiltro === "MEDICINA") return sub === "medicina" || esSanitarioGenerico
          // "material-sanitario" = solo el sanitario genérico (los de subfamilia
          // específica ya viven en sus propios filtros Cirugía/Medicina).
          if (familiaFiltro === "material-sanitario") return esSanitarioGenerico
          return familiaClasificada === familiaFiltro
        })
      }
      setFilteredProducts(filtered)
    }
  }, [debouncedSearchQuery, allProducts, familiaFiltro])

  const handleFamiliaFiltroChange = useCallback((value) => {
    setFamiliaFiltro(value)
    setFamiliaFiltroOpen(false)
  }, [])

  // Cierra el dropdown del filtro de familia al hacer click fuera.
  useEffect(() => {
    if (!familiaFiltroOpen) return
    const onClickOutside = (e) => {
      if (familiaFiltroRef.current && !familiaFiltroRef.current.contains(e.target)) {
        setFamiliaFiltroOpen(false)
      }
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [familiaFiltroOpen])

  // Cuando el selector de familia está visible, "Todos los productos" y
  // "Material Sanitario" no son opciones; si el filtro quedó en uno de esos,
  // lo movemos a la primera familia disponible (Cirugía). Solo cuando el filtro
  // se muestra, para no alterar a los clientes que usan "todos" por defecto.
  useEffect(() => {
    if (mostrarFiltroFamilia && FAMILIA_FILTRO_OCULTAS.includes(familiaFiltro)) {
      const opciones = getFamiliaFiltroOptions()
      if (opciones.length > 0) setFamiliaFiltro(opciones[0].value)
    }
  }, [mostrarFiltroFamilia, familiaFiltro])

  useEffect(() => {
    const clienteJSON = sessionStorage.getItem("activeClient")
    if (clienteJSON) {
      try {
        const clienteData = JSON.parse(clienteJSON)
        setCliente(clienteData)
        const necesitaAprobacionFlag = requiereAprobacion(clienteData.id, clienteData.clinica_matriz)
        setNecesitaAprobacion(necesitaAprobacionFlag)
        console.log("Cliente cargado:", clienteData)
        console.log("Necesita aprobación:", necesitaAprobacionFlag)
      } catch (err) {
        console.error("Error al cargar el cliente de sessionStorage:", err)
        setError("No se pudo cargar la información del cliente")
      }
    } else {
      setError("No hay un cliente seleccionado. Por favor, seleccione un cliente primero.")
      setTimeout(() => { navigate("/gestion-clientes") }, 3000)
    }
  }, [navigate])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setModalVisible(false)
      }
      if (comentariosModalRef.current && !comentariosModalRef.current.contains(event.target)) {
        setComentariosModalVisible(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => { document.removeEventListener("mousedown", handleClickOutside) }
  }, [])

  useEffect(() => {
    const cargarPedido = async () => {
      if (!cliente) return
      try {
        setLoading(true)
        const data = await makeRequest(`${API_URL}/api/pedidos/cargar`, {
          method: "POST",
          body: JSON.stringify({ cliente_id: cliente.id }),
        })
        setPedidoId(data.pedido_id)
      } catch (error) {
        console.error("Error al cargar/crear pedido:", error)
        setError("Error al inicializar el pedido. Por favor, intente de nuevo.")
      } finally {
        setLoading(false)
      }
    }
    cargarPedido()
  }, [cliente, makeRequest])

  useEffect(() => {
    const fetchProveedores = async () => {
      if (!cliente) return
      try {
        const data = await makeRequest(`${API_URL}/api/clientes/${cliente.id}/proveedores`)
        setProveedores(data.proveedores)
        const savedProveedor = sessionStorage.getItem("selectedProvider")
        if (savedProveedor) {
          const providerId = Number.parseInt(savedProveedor)
          const matchingProvider = data.proveedores.find((p) => p.id === providerId)
          if (matchingProvider) {
            setProveedorSeleccionado(matchingProvider)
            setPasoActual(2)
            cargarTodosLosProductos(providerId)
            // Tambien al restaurar el pedido en curso debemos verificar productos
            // bajo minimo y auto-anadirlos si faltan al detalle.
            cargarYReponerStockBajoMinimo(providerId)
          }
        }
      } catch (error) {
        console.error("Error al cargar proveedores:", error)
        setError("Error al cargar los proveedores del cliente")
      }
    }
    fetchProveedores()
  }, [cliente, makeRequest, cargarTodosLosProductos, cargarYReponerStockBajoMinimo])

  useEffect(() => {
    if (pedidoId) {
      setTimeout(() => { recargarLineasPedido() }, 500)
    }
  }, [pedidoId, recargarLineasPedido])

  useEffect(() => {
    const fetchComentarios = async () => {
      if (!pedidoId) return
      try {
        const data = await makeRequest(`${API_URL}/api/pedidos/${pedidoId}/comentarios-nuevo`)
        if (data && data.comentarios) {
          setComentarios(data.comentarios)
        }
      } catch (error) {
        console.error("Error al cargar comentarios:", error)
        if (error.message.includes('404')) setComentarios([])
      }
    }
    fetchComentarios()
  }, [pedidoId, makeRequest])

  const handleSearchInputChange = useCallback((e) => {
    setSearchQuery(e.target.value)
  }, [])

  const handleProveedorChange = useCallback(async (e) => {
    const providerId = Number.parseInt(e.target.value)
    const selectedProvider = proveedores.find((p) => p.id === providerId)

    if (!selectedProvider) {
      setProveedorSeleccionado(null)
      setAllProducts([])
      setFilteredProducts([])
      return
    }

    const lineasBDValidas = lineasPedidoBD.filter(linea => linea.cantidad > 0 && linea.subtotal > 0)
    const lineasTemporalesValidas = Object.values(lineasTemporales).filter(
      linea => !linea.eliminada && linea.cantidad > 0 && linea.subtotal > 0
    )

    if (lineasBDValidas.length > 0 || lineasTemporalesValidas.length > 0) {
      const confirmar = window.confirm(
        `¿Estás seguro de que quieres cambiar el proveedor del pedido?\n\n` +
        `⚠️ ADVERTENCIA: Sin finalizar el pedido, todos los productos se eliminarán de las líneas de pedido actual ya que son de proveedores distintos.\n\n` +
        `Productos actuales en el pedido: ${lineasBDValidas.length + lineasTemporalesValidas.length}`
      )
      if (!confirmar) {
        e.target.value = proveedorSeleccionado?.id || ""
        return
      }
      setLoadingCambioProveedor(true)
      try {
        if (lineasPedidoBD.length > 0) {
          for (const linea of lineasPedidoBD) {
            await makeRequest(`${API_URL}/api/pedidos/${pedidoId}/lineas`, {
              method: "POST",
              body: JSON.stringify({
                articulo_id: linea.idarticulo,
                precio: linea.precio,
                iva: linea.iva || linea.tipo_iva || 21,
                cantidad: 0,
              }),
            })
          }
        }
        if (Object.keys(lineasTemporales).length > 0) {
          clearLineasTemporales(pedidoId)
          setLineasTemporales({})
        }
        setLineasPedidoBD([])
        setTotalPedidoBD(0)
        setMensaje({ texto: "Proveedor cambiado. Productos anteriores eliminados.", tipo: "success" })
        setTimeout(() => { setMensaje({ texto: "", tipo: "" }) }, 3000)
      } catch (error) {
        console.error("Error al limpiar líneas del pedido:", error)
        setMensaje({ texto: "Error al limpiar el pedido. Intente de nuevo.", tipo: "error" })
        e.target.value = proveedorSeleccionado?.id || ""
        setLoadingCambioProveedor(false)
        return
      }
    } else {
      setLoadingCambioProveedor(true)
    }

    setProveedorSeleccionado(selectedProvider)
    sessionStorage.setItem("selectedProvider", providerId)

    if (selectedProvider) {
      setPasoActual((prev) => Math.max(prev, 2))
      try {
        await cargarTodosLosProductos(providerId)
        // Tras cargar el catalogo, comprobamos si el cliente tiene productos bajo
        // minimo para este proveedor y los reponemos automaticamente al pedido.
        cargarYReponerStockBajoMinimo(providerId)
      } catch (error) {
        console.error("Error al cargar productos:", error)
      }
    } else {
      setAllProducts([])
      setFilteredProducts([])
      setStockBajoMinimoSet(new Set())
      setStockEmbalajes({})
    }
    setTimeout(() => { setLoadingCambioProveedor(false) }, 500)
  }, [proveedores, pedidoId, lineasPedidoBD, lineasTemporales, proveedorSeleccionado, makeRequest, cargarTodosLosProductos, cargarYReponerStockBajoMinimo])

  // Vacía el detalle del pedido (BD + temporales), igual que al cambiar de
  // proveedor. Se usa al cambiar la categoría de productos del pedido.
  const limpiarLineasPedido = useCallback(async () => {
    if (lineasPedidoBD.length > 0) {
      for (const linea of lineasPedidoBD) {
        await makeRequest(`${API_URL}/api/pedidos/${pedidoId}/lineas`, {
          method: "POST",
          body: JSON.stringify({
            articulo_id: linea.idarticulo,
            precio: linea.precio,
            iva: linea.iva || linea.tipo_iva || 21,
            cantidad: 0,
          }),
        })
      }
    }
    clearLineasTemporales(pedidoId)
    setLineasTemporales({})
    setLineasPedidoBD([])
    setTotalPedidoBD(0)
  }, [lineasPedidoBD, pedidoId, makeRequest])

  // Si el pedido es de Oficina/Limpieza, el tipo debe ser SÍ o SÍ Cirugía.
  useEffect(() => {
    if (categoriaPedidoActual === "oficina_limpieza" && tipoPedido !== "cirugia_plastica") {
      setTipoPedido("cirugia_plastica")
    }
  }, [categoriaPedidoActual, tipoPedido])

  // ─── Autoderivar tipoPedido por subfamilia (clientes 131/999999) ─────
  // Si hay productos con subfamilia 'cirugia' o 'medicina' en el pedido,
  // forzamos el tipo correspondiente. El selector manual sigue activo solo
  // cuando todos los productos son 'sanitario' genérico.
  useEffect(() => {
    if (!mostrarSelectorTipo) return
    const productosActivos = []
    lineasPedidoBD.forEach(l => {
      const t = lineasTemporales[l.idarticulo]
      if (t && t.eliminada) return
      const cant = (t && !t.pendienteStock) ? t.cantidad : l.cantidad
      if (cant > 0) {
        const prod = allProducts.find(p => p.id === l.idarticulo)
        if (prod) productosActivos.push(prod)
      }
    })
    Object.values(lineasTemporales).forEach(t => {
      if (t.eliminada) return
      if (lineasPedidoBD.some(l => l.idarticulo === t.idarticulo)) return
      if ((t.cantidad > 0 || t.pendienteStock) && !t.pendienteStock) {
        const prod = allProducts.find(p => p.id === t.idarticulo)
        if (prod) productosActivos.push(prod)
      }
    })
    if (productosActivos.length === 0) return
    const res = tipoDePedidoSegunProductos(productosActivos)
    if (res.tipo && res.tipo !== tipoPedido) {
      setTipoPedido(res.tipo)
    }
  }, [mostrarSelectorTipo, lineasPedidoBD, lineasTemporales, allProducts, tipoPedido])

  const handleCantidadChange = useCallback(async (productId, cantidad) => {
    if (!pedidoId) return

    const cantidadStr = cantidad !== null && cantidad !== undefined ? cantidad.toString().trim() : ""

    // ─── Validación de compatibilidad (solo clientes 131/999999) ─────────
    // Se delega en compatibilidadAlAnadirProducto, que cubre:
    //   - Cirugía  ↔ Medicina
    //   - Medicina ↔ Limpieza / Oficina
    // El backend hace la misma validación (defensa en profundidad).
    const cantidadNumValidacion = parseFloat(cantidadStr.replace(',', '.'))
    if (Number.isFinite(cantidadNumValidacion) && cantidadNumValidacion > 0
        && clienteUsaSubfamilia(cliente?.id)) {
      const productoNuevo = allProducts.find(p => p.id === productId)
      if (productoNuevo) {
        // Unimos líneas de BD + temporales (sin contar la línea del propio
        // productId, porque puede estar incrementándose).
        const lineasActuales = [
          ...lineasPedidoBDRef.current
            .filter(l => l.idarticulo !== productId && l.cantidad > 0)
            .map(l => {
              const p = allProducts.find(pp => pp.id === l.idarticulo)
              return { familia: p?.familia, subfamilia: p?.subfamilia }
            }),
          ...Object.entries(lineasTemporalesRef.current)
            .filter(([id, l]) => parseInt(id) !== productId && l && !l.eliminada && l.cantidad > 0 && !l.pendienteStock)
            .map(([id]) => {
              const p = allProducts.find(pp => pp.id === parseInt(id))
              return { familia: p?.familia, subfamilia: p?.subfamilia }
            }),
        ]
        const conflicto = compatibilidadAlAnadirProducto(lineasActuales, productoNuevo)
        if (conflicto?.incompatible) {
          window.alert(conflicto.motivo)
          return
        }
      }
    }

    // Caso especial: linea pendienteStock. No tocamos /lineas (BD) hasta que
    // stockActual + cantidad*factor >= stockMinimo. Mientras no se llegue al
    // minimo, solo actualizamos la linea temporal localmente.
    const lineaTempActual = lineasTemporalesRef.current[productId]
    const esPendienteStock = !!(lineaTempActual && lineaTempActual.pendienteStock)

    if (esPendienteStock) {
      // Vacio/0 en una pendiente: reset local, sin confirm ni POST.
      if (!cantidadStr || cantidadStr === "" || parseFloat(cantidadStr.replace(',', '.')) === 0) {
        const pendienteReset = { ...lineaTempActual, cantidad: 0, subtotal: 0 }
        setLineaTemporal(pedidoId, productId, pendienteReset)
        setLineasTemporales(prev => ({ ...prev, [productId]: pendienteReset }))
        return
      }

      let cantidadNumPend
      try {
        cantidadNumPend = parseFloat(cantidadStr.replace(',', '.'))
        if (isNaN(cantidadNumPend) || cantidadNumPend <= 0) return
      } catch (_) { return }

      // Factor robusto. CADENA DE FALLBACK: cada paso solo cuenta como hit si
      // efectivamente devuelve un factor > 1 (parseUnidadBaseFactor devuelve 1
      // tambien cuando la entrada esta vacia, asi que no sirve como signal).
      const factorAlmacenado = parseInt(lineaTempActual.factorUnidadBase, 10)
      let factor = null
      if (Number.isFinite(factorAlmacenado) && factorAlmacenado > 1) factor = factorAlmacenado

      if (factor == null && lineaTempActual.unidadbasica) {
        const f = parseUnidadBaseFactor(lineaTempActual.unidadbasica)
        if (f > 1) factor = f
      }
      // Buscamos siempre en allProducts (es la fuente mas fiable, viene del catalogo)
      const prodCat = allProducts.find(p => p.id === productId)
      if (factor == null && prodCat && prodCat.unidadbasica) {
        const f = parseUnidadBaseFactor(prodCat.unidadbasica)
        if (f > 1) factor = f
      }
      if (factor == null) {
        const cEmbTmp = parseInt(lineaTempActual.cantidad_embalaje, 10)
        const cEmbCat = parseInt(prodCat && prodCat.cantidad_embalaje, 10)
        if (Number.isFinite(cEmbTmp) && cEmbTmp > 1) factor = cEmbTmp
        else if (Number.isFinite(cEmbCat) && cEmbCat > 1) factor = cEmbCat
      }
      if (factor == null) factor = 1
      console.log('[pendiente] factor calculado:', factor, 'desde', {
        almacenado: factorAlmacenado,
        unidadbasica_temp: lineaTempActual.unidadbasica,
        unidadbasica_cat: prodCat?.unidadbasica,
        cantidad_embalaje_temp: lineaTempActual.cantidad_embalaje,
        cantidad_embalaje_cat: prodCat?.cantidad_embalaje,
      })

      const stockActual = parseFloat(lineaTempActual.stockActual) || 0
      const stockMinimo = parseFloat(lineaTempActual.stockMinimo) || 0
      const unidadesNuevas = cantidadNumPend * factor
      const stockTras = stockActual + unidadesNuevas

      if (stockTras < stockMinimo) {
        // Aun no llega al minimo: actualizamos la pendiente localmente,
        // NO la mandamos a lpedidos todavia.
        const unidadesFaltan = Math.max(0, stockMinimo - stockTras)
        const cantidadesFaltan = Math.ceil(unidadesFaltan / factor)
        const precioPend = parseFloat(lineaTempActual.precio) || 0
        const ivaPend = lineaTempActual.tipo_iva || lineaTempActual.iva || 21
        const subtotalPrev = (precioPend * cantidadNumPend) * (1 + ivaPend / 100)
        const pendienteActualizada = {
          ...lineaTempActual,
          cantidad: cantidadNumPend,
          subtotal: subtotalPrev,
        }
        setLineaTemporal(pedidoId, productId, pendienteActualizada)
        setLineasTemporales(prev => ({ ...prev, [productId]: pendienteActualizada }))
        const msg = factor > 1
          ? `Aún faltan ${cantidadesFaltan} cantidad(es) (${unidadesFaltan.toFixed(0)} unidad(es)) para alcanzar el mínimo.`
          : `Aún faltan ${unidadesFaltan.toFixed(0)} unidad(es) para alcanzar el mínimo.`
        setMensaje({ texto: msg, tipo: 'info' })
        setTimeout(() => setMensaje({ texto: '', tipo: '' }), 4000)
        return
      }

      // stockTras >= minimo: convertimos la pendiente en linea real y la
      // mandamos a /lineas usando los datos guardados en la pendiente
      // (precio, iva, nombre, etc). No dependemos de allProducts/lineasPedidoBD
      // porque el producto puede no estar en ninguno de los dos.
      const precioReal = parseFloat(lineaTempActual.precio) || 0
      const ivaReal = lineaTempActual.tipo_iva || lineaTempActual.iva || 21
      if (precioReal <= 0) {
        console.warn('[pendiente->real] precio invalido, se cancela el POST')
        return
      }
      const subtotalReal = (precioReal * cantidadNumPend) * (1 + ivaReal / 100)
      const lineaRealTemp = {
        id: `temp_${productId}`,
        idarticulo: productId,
        nombre: lineaTempActual.nombre,
        ref_proveedor: lineaTempActual.ref_proveedor,
        raiz: lineaTempActual.raiz,
        precio: precioReal,
        cantidad: cantidadNumPend,
        iva: ivaReal,
        tipo_iva: ivaReal,
        subtotal: subtotalReal,
        cantidad_embalaje: lineaTempActual.cantidad_embalaje,
        temporal: true,
      }
      setLineaTemporal(pedidoId, productId, lineaRealTemp)
      setLineasTemporales(prev => ({ ...prev, [productId]: lineaRealTemp }))
      setMensaje({
        texto: `Cantidad suficiente para alcanzar el mínimo de stock. Añadiendo al pedido…`,
        tipo: 'success'
      })
      setTimeout(() => setMensaje({ texto: '', tipo: '' }), 3000)

      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current)
      if (saveQueueRef.current.has(productId)) return
      saveQueueRef.current.add(productId)
      updateTimeoutRef.current = setTimeout(async () => {
        try {
          await makeRequest(`${API_URL}/api/pedidos/${pedidoId}/lineas`, {
            method: "POST",
            body: JSON.stringify({
              articulo_id: productId,
              precio: precioReal,
              iva: ivaReal,
              cantidad: cantidadNumPend,
            }),
          })
          setTimeout(async () => {
            await recargarLineasPedido()
            setTimeout(() => {
              removeLineaTemporal(pedidoId, productId)
              setLineasTemporales(prev => {
                const next = { ...prev }
                delete next[productId]
                return next
              })
            }, 500)
          }, 1500)
        } catch (error) {
          console.error('Error al guardar pendiente promovida:', error)
          // Si el backend rechazó por mezcla incompatible (cirugía/medicina/limpieza/oficina),
          // mostramos el motivo exacto y NO el genérico "Error al guardar producto".
          if (error.status === 409 && error.body?.error === 'incompatible_subfamilia') {
            window.alert(error.body.message || 'Este producto no es compatible con los productos actuales del pedido.')
          } else if (!error.message.includes('404')) {
            setMensaje({ texto: 'Error al guardar producto', tipo: 'error' })
          }
        } finally {
          saveQueueRef.current.delete(productId)
        }
      }, 1000)
      return
    }

    // Aviso: si el producto esta bajo minimo en stock, confirmar antes de eliminar la linea
    if ((!cantidadStr || cantidadStr === "" || parseInt(cantidadStr) === 0) && stockBajoMinimoSet.has(productId) && !esPendienteStock) {
      const confirmar = window.confirm(
        '⚠️ Este producto está bajo el mínimo de stock.\n\n' +
        'Si lo eliminas del pedido, el stock se quedará por debajo del mínimo y volverá a aparecer ' +
        'automáticamente en el siguiente pedido a este proveedor.\n\n' +
        '¿Continuar eliminando del pedido?'
      )
      if (!confirmar) return
    }

    if (!cantidadStr || cantidadStr === "") {
      let producto = allProducts.find(p => p.id === productId)
      let precio, tipoIva

      if (producto) {
        precio = producto.precio
        tipoIva = producto.tipo_iva !== undefined && producto.tipo_iva !== null ? producto.tipo_iva : 21
      } else {
        const lineaExistente = lineasPedidoBD.find(linea => linea.idarticulo === productId)
        if (lineaExistente) {
          precio = lineaExistente.precio
          tipoIva = lineaExistente.tipo_iva || lineaExistente.iva || 21
          producto = {
            id: productId,
            nombre: lineaExistente.nombre,
            ref_proveedor: lineaExistente.ref_proveedor,
            raiz: lineaExistente.raiz,
            precio: precio,
            tipo_iva: tipoIva,
            cantidad_embalaje: lineaExistente.cantidad_embalaje
          }
        } else {
          console.error("No se encontró información del producto para eliminar")
          return
        }
      }

      const existeEnBD = lineasPedidoBD.find(linea => linea.idarticulo === productId)

      if (existeEnBD) {
        const lineaEliminada = {
          id: `temp_${productId}`,
          idarticulo: productId,
          nombre: producto.nombre,
          ref_proveedor: producto.ref_proveedor,
          raiz: producto.raiz,
          precio: precio,
          cantidad: 0,
          iva: tipoIva,
          tipo_iva: tipoIva,
          subtotal: 0,
          cantidad_embalaje: producto.cantidad_embalaje,
          temporal: true,
          eliminada: true
        }
        setLineaTemporal(pedidoId, productId, lineaEliminada)
        setLineasTemporales(prev => ({ ...prev, [productId]: lineaEliminada }))
      } else {
        removeLineaTemporal(pedidoId, productId)
        setLineasTemporales(prev => {
          const newState = { ...prev }
          delete newState[productId]
          return newState
        })
      }

      if (existeEnBD) {
        if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current)
        if (saveQueueRef.current.has(productId)) return
        saveQueueRef.current.add(productId)
        updateTimeoutRef.current = setTimeout(async () => {
          try {
            await makeRequest(`${API_URL}/api/pedidos/${pedidoId}/lineas`, {
              method: "POST",
              body: JSON.stringify({
                articulo_id: productId,
                precio: precio,
                iva: tipoIva,
                cantidad: 0,
              }),
            })
            setTimeout(async () => {
              await recargarLineasPedido()
              setTimeout(() => {
                removeLineaTemporal(pedidoId, productId)
                setLineasTemporales(prev => {
                  const newState = { ...prev }
                  delete newState[productId]
                  return newState
                })
              }, 500)
            }, 1500)
          } catch (error) {
            console.error("Error al eliminar producto:", error)
            if (!error.message.includes('404')) {
              setMensaje({ texto: "Error al eliminar producto", tipo: "error" })
            }
          } finally {
            saveQueueRef.current.delete(productId)
          }
        }, 1000)
      }
      return
    }

    let cantidadNum
    try {
      const cantidadNormalizada = cantidadStr.replace(',', '.')
      cantidadNum = parseFloat(cantidadNormalizada)
      if (isNaN(cantidadNum) || cantidadNum <= 0) {
        console.warn(`Cantidad inválida recibida: "${cantidadStr}" -> ${cantidadNum}`)
        return
      }
      if (cantidadNum.toString() === "1" && cantidadStr !== "1" && cantidadStr !== "1.0") {
        console.warn(`Posible error de parsing: "${cantidadStr}" se parseó como ${cantidadNum}`)
        const numeroLimpio = cantidadStr.match(/^(\d+\.?\d*)$/)
        if (numeroLimpio) {
          cantidadNum = parseFloat(numeroLimpio[1])
          console.log(`Parsing corregido: "${cantidadStr}" -> ${cantidadNum}`)
        } else {
          console.error(`No se pudo parsear correctamente: "${cantidadStr}"`)
          return
        }
      }
    } catch (error) {
      console.error(`Error al parsear cantidad "${cantidadStr}":`, error)
      return
    }

    console.log(`Procesando cantidad: "${cantidadStr}" -> ${cantidadNum}`)

    // La validación de mezcla cirugía/medicina y medicina/limpieza-oficina ya
    // se realiza al principio del handler (clientes 131/999999) vía
    // compatibilidadAlAnadirProducto. Sanitario y Limpieza/Oficina SÍ pueden
    // convivir en el mismo pedido — el pedido resultante será de Cirugía.

    let producto = allProducts.find(p => p.id === productId)
    let precio, tipoIva

    if (producto) {
      precio = producto.precio
      tipoIva = producto.tipo_iva !== undefined && producto.tipo_iva !== null ? producto.tipo_iva : 21
    } else {
      const lineaExistente = lineasPedidoBD.find(linea => linea.idarticulo === productId)
      if (lineaExistente) {
        precio = lineaExistente.precio
        tipoIva = lineaExistente.tipo_iva || lineaExistente.iva || 21
        producto = {
          id: productId,
          nombre: lineaExistente.nombre,
          ref_proveedor: lineaExistente.ref_proveedor,
          raiz: lineaExistente.raiz,
          precio: precio,
          tipo_iva: tipoIva,
          cantidad_embalaje: lineaExistente.cantidad_embalaje
        }
      } else {
        console.error("No se encontró información del producto")
        return
      }
    }

    if (precio <= 0) return

    const subtotalCalculado = (precio * cantidadNum) * (1 + (tipoIva / 100))
    if (subtotalCalculado <= 0) {
      console.error("Subtotal calculado es 0 o negativo, no se procesará")
      return
    }

    console.log(`Guardando producto ${productId} con cantidad ${cantidadNum}, subtotal ${subtotalCalculado}`)

    const lineaTemporal = {
      id: `temp_${productId}`,
      idarticulo: productId,
      nombre: producto.nombre,
      ref_proveedor: producto.ref_proveedor,
      raiz: producto.raiz,
      precio: precio,
      cantidad: cantidadNum,
      iva: tipoIva,
      tipo_iva: tipoIva,
      subtotal: subtotalCalculado,
      cantidad_embalaje: producto.cantidad_embalaje,
      temporal: true
    }

    setLineaTemporal(pedidoId, productId, lineaTemporal)
    setLineasTemporales(prev => ({ ...prev, [productId]: lineaTemporal }))

    if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current)
    if (saveQueueRef.current.has(productId)) return
    saveQueueRef.current.add(productId)

    updateTimeoutRef.current = setTimeout(async () => {
      try {
        if (!pedidoId) { console.error("PedidoId no disponible"); return }
        console.log(`Enviando al backend: cantidad ${cantidadNum}`)
        await makeRequest(`${API_URL}/api/pedidos/${pedidoId}/lineas`, {
          method: "POST",
          body: JSON.stringify({
            articulo_id: productId,
            precio: precio,
            iva: tipoIva,
            cantidad: cantidadNum,
          }),
        })
        setTimeout(async () => {
          await recargarLineasPedido()
          setTimeout(() => {
            removeLineaTemporal(pedidoId, productId)
            setLineasTemporales(prev => {
              const newState = { ...prev }
              delete newState[productId]
              return newState
            })
          }, 500)
        }, 1500)
      } catch (error) {
        console.error("Error al guardar en BD:", error)
        // 409 incompatible_subfamilia → alert con el motivo exacto del backend.
        if (error.status === 409 && error.body?.error === 'incompatible_subfamilia') {
          window.alert(error.body.message || 'Este producto no es compatible con los productos actuales del pedido.')
          // Limpiamos la línea temporal que quedó "fantasma" tras el rechazo.
          removeLineaTemporal(pedidoId, productId)
          setLineasTemporales(prev => {
            const next = { ...prev }
            delete next[productId]
            return next
          })
        } else if (!error.message.includes('404')) {
          setMensaje({ texto: "Error al guardar producto", tipo: "error" })
        }
      } finally {
        saveQueueRef.current.delete(productId)
      }
    }, 1000)
  }, [pedidoId, allProducts, lineasPedidoBD, makeRequest, recargarLineasPedido, stockBajoMinimoSet, categoriaPedidoActual, limpiarLineasPedido])

  const handleEliminarLinea = useCallback(async (linea) => {
    if (!pedidoId) return
    const confirmar = window.confirm(`¿Está seguro de que desea eliminar "${linea.nombre}" del pedido?`)
    if (!confirmar) return

    if (linea.temporal && !linea.eliminada) {
      removeLineaTemporal(pedidoId, linea.idarticulo)
      setLineasTemporales(prev => {
        const newState = { ...prev }
        delete newState[linea.idarticulo]
        return newState
      })
      return
    }

    const lineaEliminada = {
      id: `temp_${linea.idarticulo}`,
      idarticulo: linea.idarticulo,
      nombre: linea.nombre,
      ref_proveedor: linea.ref_proveedor,
      raiz: linea.raiz,
      precio: linea.precio,
      cantidad: 0,
      iva: linea.iva || linea.tipo_iva || 21,
      tipo_iva: linea.tipo_iva || linea.iva || 21,
      subtotal: 0,
      cantidad_embalaje: linea.cantidad_embalaje,
      temporal: true,
      eliminada: true
    }

    setLineaTemporal(pedidoId, linea.idarticulo, lineaEliminada)
    setLineasTemporales(prev => ({ ...prev, [linea.idarticulo]: lineaEliminada }))

    try {
      await makeRequest(`${API_URL}/api/pedidos/${pedidoId}/lineas`, {
        method: "POST",
        body: JSON.stringify({
          articulo_id: linea.idarticulo,
          precio: linea.precio,
          iva: linea.iva || linea.tipo_iva || 21,
          cantidad: 0,
        }),
      })
      setMensaje({ texto: "Producto eliminado", tipo: "success" })
      setTimeout(() => { setMensaje({ texto: "", tipo: "" }) }, 2000)
      setTimeout(async () => {
        await recargarLineasPedido()
        setTimeout(() => {
          removeLineaTemporal(pedidoId, linea.idarticulo)
          setLineasTemporales(prev => {
            const newState = { ...prev }
            delete newState[linea.idarticulo]
            return newState
          })
        }, 500)
      }, 1500)
    } catch (error) {
      console.error("Error al eliminar línea:", error)
      if (!error.message.includes('404')) {
        setMensaje({ texto: "Error al eliminar producto", tipo: "error" })
        removeLineaTemporal(pedidoId, linea.idarticulo)
        setLineasTemporales(prev => {
          const newState = { ...prev }
          delete newState[linea.idarticulo]
          return newState
        })
      }
    }
  }, [pedidoId, lineasPedidoBD, totalPedidoBD, makeRequest, recargarLineasPedido])

  const lineasCombinadas = useMemo(() => {
    const combinadas = []
    lineasPedidoBD.forEach(lineaBD => {
      const lineaTemporal = lineasTemporales[lineaBD.idarticulo]
      // Una pendienteStock NO es un override real de cantidad — es solo un marcador
      // visual cuando NO hay linea en BD. Si la hay, mostrar la BD normalmente.
      if (lineaTemporal && !lineaTemporal.pendienteStock) {
        if (lineaTemporal.eliminada) return
        if (lineaTemporal.cantidad > 0 && lineaTemporal.subtotal > 0) {
          combinadas.push({ ...lineaBD, cantidad: lineaTemporal.cantidad, subtotal: lineaTemporal.subtotal })
        }
      } else {
        if (lineaBD.cantidad > 0 && lineaBD.subtotal > 0) combinadas.push(lineaBD)
      }
    })
    Object.values(lineasTemporales).forEach(lineaTemporal => {
      const existeEnBD = lineasPedidoBD.find(linea => linea.idarticulo === lineaTemporal.idarticulo)
      if (existeEnBD || lineaTemporal.eliminada) return
      // Pendiente por stock bajo: se muestra aunque cantidad sea 0 (SOLO si no hay BD).
      // Enriquecemos con unidadbasica/cantidad_embalaje del catalogo si falta — asi
      // se muestra "(1 caja de 100)" entre parentesis aunque el sessionStorage sea viejo.
      if (lineaTemporal.pendienteStock) {
        const prodCat = allProducts.find(p => p.id === lineaTemporal.idarticulo)
        const enriquecida = prodCat ? {
          ...lineaTemporal,
          unidadbasica: lineaTemporal.unidadbasica || prodCat.unidadbasica || '',
          cantidad_embalaje: lineaTemporal.cantidad_embalaje || prodCat.cantidad_embalaje,
        } : lineaTemporal
        combinadas.push(enriquecida)
        return
      }
      if (lineaTemporal.cantidad > 0 && lineaTemporal.subtotal > 0) {
        combinadas.push(lineaTemporal)
      }
    })
    return combinadas
  }, [lineasPedidoBD, lineasTemporales, allProducts])

  const totalCombinado = useMemo(() => {
    return lineasCombinadas.reduce((total, linea) => total + linea.subtotal, 0)
  }, [lineasCombinadas])

  const validacionPrecioMinimo = useMemo(() => {
    if (!proveedorSeleccionado || !proveedorSeleccionado.precio_min) {
      return { esValido: true, mensaje: "" }
    }
    const precioMinimo = Number.parseFloat(proveedorSeleccionado.precio_min)
    if (precioMinimo <= 0) return { esValido: true, mensaje: "" }
    if (totalCombinado < precioMinimo) {
      return {
        esValido: false,
        mensaje: `El pedido debe alcanzar un mínimo de ${new Intl.NumberFormat("es-ES", {
          style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2,
        }).format(precioMinimo)}. Total actual: ${new Intl.NumberFormat("es-ES", {
          style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2,
        }).format(totalCombinado)}`
      }
    }
    return { esValido: true, mensaje: "" }
  }, [proveedorSeleccionado, totalCombinado])

  const getCantidadMostrada = useCallback((productId) => {
    if (lineasTemporales[productId]) {
      if (lineasTemporales[productId].eliminada) return 0
      return lineasTemporales[productId].cantidad
    }
    const lineaBD = lineasPedidoBD.find(linea => linea.idarticulo === productId)
    return lineaBD ? lineaBD.cantidad : 0
  }, [lineasTemporales, lineasPedidoBD])

  const handlePrecioChange = useCallback(async (precioId, nuevoPrecio) => {
    if (user.permisos >= 3) return
    try {
      await makeRequest(`${API_URL}/api/precios/${precioId}`, {
        method: "PUT",
        body: JSON.stringify({ precio: nuevoPrecio }),
      })
      setMensaje({ texto: "Precio actualizado", tipo: "success" })
      if (proveedorSeleccionado) {
        setTimeout(() => { cargarTodosLosProductos(proveedorSeleccionado.id) }, 300)
      }
    } catch (error) {
      console.error("Error al actualizar precio:", error)
      setMensaje({ texto: "Error al actualizar precio", tipo: "error" })
    }
  }, [user.permisos, makeRequest, cargarTodosLosProductos, proveedorSeleccionado])

  const handleAddComentario = useCallback(async (e) => {
    e.preventDefault()
    if (!pedidoId || !nuevoComentario.trim()) return
    try {
      const data = await makeRequest(`${API_URL}/api/pedidos/${pedidoId}/comentarios-nuevo`, {
        method: "POST",
        body: JSON.stringify({
          comentario: nuevoComentario,
          cantidad: cantidadComentario || 0,
          precio: precioComentario || 0,
        }),
      })
      if (data && data.comentarios) {
        setComentarios(data.comentarios)
        setNuevoComentario("")
        setCantidadComentario("")
        setPrecioComentario("")
      }
    } catch (error) {
      console.error("Error al agregar comentario:", error)
      if (!error.message.includes('404')) {
        setMensaje({ texto: "Error al agregar comentario", tipo: "error" })
      }
    }
  }, [pedidoId, nuevoComentario, cantidadComentario, precioComentario, makeRequest])

  const handleDeleteComentario = useCallback(async (comentarioId) => {
    if (!pedidoId) return
    try {
      const data = await makeRequest(`${API_URL}/api/pedidos/${pedidoId}/comentarios-nuevo/${comentarioId}`, {
        method: "DELETE",
      })
      if (data && data.comentarios) setComentarios(data.comentarios)
    } catch (error) {
      console.error("Error al eliminar comentario:", error)
      if (!error.message.includes('404')) {
        setMensaje({ texto: "Error al eliminar comentario", tipo: "error" })
      }
    }
  }, [pedidoId, makeRequest])

  const handleFinalizarPedido = useCallback(async (e) => {
    e.preventDefault()
    if (!pedidoId || !proveedorSeleccionado) {
      setMensaje({ texto: "Debe seleccionar un proveedor", tipo: "error" })
      return
    }
    if (lineasCombinadas.length === 0) {
      setMensaje({ texto: "El pedido no puede estar vacío", tipo: "error" })
      return
    }
    if (!validacionPrecioMinimo.esValido) {
      setMensaje({ texto: validacionPrecioMinimo.mensaje, tipo: "error" })
      return
    }
    if (mostrarSelectorTipo && !tipoPedidoEsAutoderivado && !tipoPedido) {
      setMensaje({ texto: "Debe seleccionar el tipo de pedido (Cirugía Plástica o Medicina Estética)", tipo: "error" })
      return
    }

    if (Object.keys(lineasTemporales).length > 0) {
      const lineasPendientes = Object.values(lineasTemporales).filter(linea => !linea.eliminada)
      const lineasEliminar = Object.values(lineasTemporales).filter(linea => linea.eliminada)
      if (lineasPendientes.length > 0 || lineasEliminar.length > 0) {
        for (const [productId, linea] of Object.entries(lineasTemporales)) {
          // Pendiente por stock bajo que quedo en 0: es solo un aviso, NO una linea
          // del pedido. Se ignora para no crear lineas basura con cantidad 0.
          if (linea.pendienteStock && !linea.eliminada && (!linea.cantidad || linea.cantidad <= 0)) {
            continue
          }
          try {
            await makeRequest(`${API_URL}/api/pedidos/${pedidoId}/lineas`, {
              method: "POST",
              body: JSON.stringify({
                articulo_id: productId,
                precio: linea.precio,
                iva: linea.iva,
                cantidad: linea.eliminada ? 0 : linea.cantidad,
              }),
            })
          } catch (error) {
            console.error("Error al guardar línea temporal:", error)
          }
        }
        clearLineasTemporales(pedidoId)
        setLineasTemporales({})
      }
    }

    try {
      setLoading(true)
      // Cualquier variante de "S/N" (incluida la anotacion "S/N cirugia" que
      // teclea el personal) se envia como "S/N" para que el backend derive el
      // id del pedido. Solo se manda texto si es un numero real del cliente.
      const rawNpedido = npedidoCliente.trim()
      const numeroPedidoAEnviar = (rawNpedido === "" || /^s\/?n/i.test(rawNpedido)) ? "S/N" : rawNpedido
      const bodyData = {
        npedidoc: numeroPedidoAEnviar,
        proveedor_id: proveedorSeleccionado.id,
      }
      if (mostrarSelectorTipo) bodyData.tipo = tipoPedido

      await makeRequest(`${API_URL}/api/pedidos/${pedidoId}/finalizar`, {
        method: "POST",
        body: JSON.stringify(bodyData),
      })
      setModalVisible(false)

      if (necesitaAprobacion) {
        setMensaje({ texto: "Pedido enviado para aprobación de la clínica matriz", tipo: "success" })
      } else {
        setMensaje({ texto: "Pedido finalizado correctamente", tipo: "success" })
      }

      setTimeout(() => {
        navigate("/finalizar-pedido", {
          state: {
            pedidoId,
            npedidoCliente: numeroPedidoAEnviar,
            proveedorId: proveedorSeleccionado.id,
            necesitaAprobacion: necesitaAprobacion,
            tipo: mostrarSelectorTipo ? tipoPedido : null,
          },
        })
      }, 500)
    } catch (error) {
      console.error("Error al finalizar pedido:", error)
      setMensaje({ texto: "Error al finalizar pedido", tipo: "error" })
      setLoading(false)
    }
  }, [pedidoId, proveedorSeleccionado, lineasCombinadas, npedidoCliente, validacionPrecioMinimo, makeRequest, navigate, lineasTemporales, necesitaAprobacion, mostrarSelectorTipo, tipoPedido, tipoPedidoEsAutoderivado])

  const handleOpenModal = useCallback(() => {
    if (!proveedorSeleccionado || lineasCombinadas.length === 0) {
      if (!proveedorSeleccionado) {
        setMensaje({ texto: "Debe seleccionar un proveedor", tipo: "error" })
      } else {
        setMensaje({ texto: "El pedido no puede estar vacío", tipo: "error" })
      }
      return
    }
    if (!validacionPrecioMinimo.esValido) {
      setMensaje({ texto: validacionPrecioMinimo.mensaje, tipo: "error" })
      return
    }
    setModalVisible(true)
  }, [proveedorSeleccionado, lineasCombinadas.length, validacionPrecioMinimo])

  const handleSolicitarProducto = useCallback((referencia, nombre) => {
    setNuevoComentario(`${referencia} - ${nombre}`)
    setComentariosModalVisible(true)
    setTimeout(() => { document.getElementById("ccc")?.focus() }, 100)
  }, [])

  // ─── REPETICIÓN DE PEDIDOS ────────────────────────────────────────────
  const cargarPedidosAnteriores = useCallback(async () => {
    if (!cliente) return
    try {
      setLoadingRepetir(true)
      const data = await makeRequest(`${API_URL}/api/pedidos/anteriores/${cliente.id}`)
      setPedidosAnteriores(data || [])
    } catch (error) {
      console.error("Error al cargar pedidos anteriores:", error)
      setPedidosAnteriores([])
    } finally {
      setLoadingRepetir(false)
    }
  }, [cliente, makeRequest])

  const cargarPedidosFavoritos = useCallback(async () => {
    if (!cliente) return
    try {
      setLoadingRepetir(true)
      const data = await makeRequest(`${API_URL}/api/pedidos/favoritos/${cliente.id}`)
      setPedidosFavoritos(data || [])
    } catch (error) {
      console.error("Error al cargar pedidos favoritos:", error)
      setPedidosFavoritos([])
    } finally {
      setLoadingRepetir(false)
    }
  }, [cliente, makeRequest])

  useEffect(() => {
    if (tabIzquierda === 'repetir') {
      if (tabRepetir === 'anteriores') {
        cargarPedidosAnteriores()
      } else {
        cargarPedidosFavoritos()
      }
    }
  }, [tabIzquierda, tabRepetir, cargarPedidosAnteriores, cargarPedidosFavoritos])

  // ─── PRODUCTOS FAVORITOS (por clínica) ────────────────────────────────
  const cargarFavoritos = useCallback(async () => {
    if (!cliente) { setFavoritosIds(new Set()); return }
    try {
      const data = await makeRequest(`${API_URL}/api/productos-favoritos/${cliente.id}`)
      setFavoritosIds(new Set((data?.ids || []).map(Number)))
    } catch (error) {
      console.error("Error al cargar favoritos:", error)
    }
  }, [cliente, makeRequest])

  useEffect(() => { cargarFavoritos() }, [cargarFavoritos])

  const handleToggleFavoritoProducto = useCallback(async (productoId) => {
    if (!cliente) return
    const id = parseInt(productoId)
    // Optimista: cambiamos el estado ya y revertimos si falla.
    setFavoritosIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
    try {
      await makeRequest(`${API_URL}/api/productos-favoritos/toggle`, {
        method: "POST",
        body: JSON.stringify({ cliente_id: cliente.id, producto_id: id }),
      })
    } catch (error) {
      console.error("Error al cambiar favorito:", error)
      setFavoritosIds(prev => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id); else next.add(id)
        return next
      })
    }
  }, [cliente, makeRequest])

  // Productos favoritos disponibles para el proveedor seleccionado (los que
  // están en el catálogo ya cargado y marcados como favoritos).
  const favoritosProductos = useMemo(
    () => allProducts.filter(p => favoritosIds.has(p.id)),
    [allProducts, favoritosIds]
  )

  const toggleExpandPedido = useCallback((pedidoId) => {
    setExpandedPedidos(prev => {
      const next = new Set(prev)
      if (next.has(pedidoId)) {
        next.delete(pedidoId)
      } else {
        next.add(pedidoId)
      }
      return next
    })
  }, [])

  const handleToggleFavorito = useCallback(async (pedidoIdFav) => {
    try {
      const data = await makeRequest(`${API_URL}/api/pedidos/${pedidoIdFav}/favorito`, {
        method: "PUT",
      })
      if (data.success) {
        // Actualizar en la lista de anteriores
        setPedidosAnteriores(prev => prev.map(p =>
          p.id === pedidoIdFav ? { ...p, favorito: data.favorito } : p
        ))
        // Actualizar en la lista de favoritos
        if (data.favorito === 0) {
          setPedidosFavoritos(prev => prev.filter(p => p.id !== pedidoIdFav))
        } else {
          cargarPedidosFavoritos()
        }
        setMensaje({
          texto: data.favorito === 1 ? "Pedido añadido a favoritos" : "Pedido eliminado de favoritos",
          tipo: "success"
        })
        setTimeout(() => setMensaje({ texto: "", tipo: "" }), 2000)
      }
    } catch (error) {
      console.error("Error al toggle favorito:", error)
      setMensaje({ texto: "Error al actualizar favorito", tipo: "error" })
    }
  }, [makeRequest, cargarPedidosFavoritos])

  const handleCopiarLineas = useCallback(async (sourcePedidoId) => {
    if (!pedidoId) return
    const confirmar = window.confirm(
      "¿Deseas añadir las líneas de este pedido al pedido actual?\n\n" +
      "Si el proveedor es distinto, se cambiará el proveedor y se eliminarán las líneas actuales."
    )
    if (!confirmar) return

    try {
      setLoadingCopiar(true)
      const data = await makeRequest(`${API_URL}/api/pedidos/${pedidoId}/copiar-lineas`, {
        method: "POST",
        body: JSON.stringify({ source_pedido_id: sourcePedidoId }),
      })

      if (data.success) {
        // Si cambió el proveedor, actualizar el estado local
        if (data.proveedor_cambiado) {
          // Buscar el proveedor del pedido fuente
          const pedidoFuente = [...pedidosAnteriores, ...pedidosFavoritos].find(p => p.id === sourcePedidoId)
          if (pedidoFuente) {
            const nuevoProveedor = proveedores.find(p => p.id === pedidoFuente.proveedor_id)
            if (nuevoProveedor) {
              setProveedorSeleccionado(nuevoProveedor)
              sessionStorage.setItem("selectedProvider", nuevoProveedor.id)
              cargarTodosLosProductos(nuevoProveedor.id)
            }
          }
          // Limpiar líneas temporales ya que se borraron
          clearLineasTemporales(pedidoId)
          setLineasTemporales({})
        }

        // Recargar líneas del pedido
        await recargarLineasPedido()

        let mensajeTexto = `${data.lineas_copiadas.length} líneas copiadas al pedido`
        if (data.lineas_omitidas && data.lineas_omitidas.length > 0) {
          mensajeTexto += `. ${data.lineas_omitidas.length} líneas omitidas`
        }
        if (data.proveedor_cambiado) {
          mensajeTexto += ". Proveedor actualizado"
        }
        setMensaje({ texto: mensajeTexto, tipo: "success" })
        setTimeout(() => setMensaje({ texto: "", tipo: "" }), 4000)

        // Volver al catálogo para que vea el detalle
        setTabIzquierda('catalogo')
      }
    } catch (error) {
      console.error("Error al copiar líneas:", error)
      setMensaje({ texto: "Error al copiar líneas del pedido", tipo: "error" })
    } finally {
      setLoadingCopiar(false)
    }
  }, [pedidoId, pedidosAnteriores, pedidosFavoritos, proveedores, makeRequest, recargarLineasPedido, cargarTodosLosProductos])
  // ─── FIN REPETICIÓN DE PEDIDOS ────────────────────────────────────────

  const productosPorFamilia = useMemo(() => {
    if (!filteredProducts || filteredProducts.length === 0) return {}
    const agrupados = {}
    filteredProducts.forEach((producto) => {
      // Con "favoritos primero" activo, los favoritos van a un grupo sintético
      // al principio en vez de a su familia.
      if (verFavoritosPrimero && favoritosIds.has(producto.id)) {
        if (!agrupados[FAVORITOS_FAMILIA]) agrupados[FAVORITOS_FAMILIA] = []
        agrupados[FAVORITOS_FAMILIA].push(producto)
        return
      }
      const familia = producto.familia || ""
      if (!agrupados[familia]) agrupados[familia] = []
      agrupados[familia].push(producto)
    })
    return agrupados
  }, [filteredProducts, verFavoritosPrimero, favoritosIds])

  const familias = useMemo(() => {
    return Object.keys(productosPorFamilia).sort((a, b) => {
      if (a === FAVORITOS_FAMILIA) return -1 // el grupo de favoritos siempre arriba
      if (b === FAVORITOS_FAMILIA) return 1
      if (a === "") return 1
      if (b === "") return -1
      return a.localeCompare(b)
    })
  }, [productosPorFamilia])

  // Pendientes sin resolver: lineas temporales con pendienteStock=true que el user
  // todavia no ha resuelto (ni meter cantidad ni eliminar explicitamente).
  const pendientesSinResolver = useMemo(() => {
    return Object.values(lineasTemporales).filter(
      l => l.pendienteStock && !l.eliminada && (!l.cantidad || l.cantidad <= 0)
    )
  }, [lineasTemporales])

  const botonFinalizarDesactivado = useMemo(() => {
    if (!proveedorSeleccionado) return true
    if (lineasCombinadas.length === 0) return true
    if (!validacionPrecioMinimo.esValido) return true
    // Los pendientes por stock bajo (aviso) YA NO bloquean el envio: el pedido se
    // puede enviar tengan o no cantidad. Los que queden en 0 se ignoran al enviar
    // (ver handleFinalizarPedido: se saltan en el POST de lineas).
    return false
  }, [proveedorSeleccionado, lineasCombinadas.length, validacionPrecioMinimo.esValido])

  // ─── LOADING SCREENS ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex justify-center items-center">
        <div className="text-center">
          <div className="relative mb-8">
            <div
              className="w-20 h-20 border-4 rounded-full animate-spin mx-auto"
              style={{
                borderColor: `${theme.primary}40`,
                borderTopColor: theme.primary,
              }}
            ></div>
            <ShoppingCart
              className="w-8 h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              style={textPrimary}
            />
          </div>
          <h3 className="text-xl font-bold mb-2" style={textPrimary}>Preparando pedido</h3>
          <p className="text-gray-600">Inicializando gestión de pedidos...</p>
        </div>
      </div>
    )
  }

  if (loadingCambioProveedor) {
    return (
      <div className="min-h-screen bg-white flex justify-center items-center">
        <div className="text-center">
          <div className="relative mb-8">
            <div
              className="w-20 h-20 border-4 rounded-full animate-spin mx-auto"
              style={{
                borderColor: `${theme.primary}40`,
                borderTopColor: theme.primary,
              }}
            ></div>
            <ShoppingCart
              className="w-8 h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              style={textPrimary}
            />
          </div>
          <h3 className="text-xl font-bold mb-2" style={textPrimary}>Cambiando de proveedor</h3>
          <p className="text-gray-600">Eliminando pedido anterior y añadiendo nuevo proveedor</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-full py-12">
        <div className="bg-red-100 text-red-800 p-4 rounded-md shadow-md">
          <p className="font-medium">Error:</p>
          <p>{error}</p>
        </div>
      </div>
    )
  }

  // ─── RENDER PRINCIPAL ─────────────────────────────────────────────────
  return (
    <div className="nuevo-pedido-root">

      {/* ── Header Principal ──────────────────────────────────────────── */}
      <div className="nuevo-pedido-header">
        <div className="flex items-center space-x-4">
          <div className="p-2.5 rounded-full" style={bgPrimaryLight}>
            <ShoppingCart className="w-5 h-5" style={textPrimary} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Nuevo Pedido {pedidoId ? `#${pedidoId}` : ""}
            </h1>
            <p className="text-sm text-gray-500">{nombreClienteActivo}</p>
          </div>
          {necesitaAprobacion && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
              <Clock size={12} className="mr-1.5" />
              Requiere aprobación
            </span>
          )}
        </div>

        {mensaje.texto && (
          <div
            className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium border animate-fadeIn ${
              mensaje.tipo === "error"
                ? "bg-red-50 text-red-700 border-red-200"
                : "bg-green-50 text-green-700 border-green-200"
            }`}
          >
            {mensaje.tipo === "error" ? (
              <AlertTriangle size={15} className="mr-2" />
            ) : (
              <CheckCircle size={15} className="mr-2" />
            )}
            {mensaje.texto}
          </div>
        )}
      </div>

      {/* ── Layout principal: izquierda + derecha ─────────────────────── */}
      <div className="nuevo-pedido-body">

        {/* ── PANEL IZQUIERDO: Productos ───────────────────────────────── */}
        <div className="nuevo-pedido-left">

          {/* Tabs: Catálogo / Repetir Pedido */}
          <div className="panel-section" style={{ paddingBottom: 0 }}>
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setTabIzquierda('catalogo')}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  tabIzquierda === 'catalogo'
                    ? 'border-current'
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
                style={tabIzquierda === 'catalogo' ? { color: theme.primary, borderColor: theme.primary } : {}}
              >
                <Package size={15} />
                Catálogo
              </button>
              <button
                onClick={() => setTabIzquierda('repetir')}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  tabIzquierda === 'repetir'
                    ? 'border-current'
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
                style={tabIzquierda === 'repetir' ? { color: theme.primary, borderColor: theme.primary } : {}}
              >
                <RotateCcw size={15} />
                Repetir Pedido
              </button>
              <button
                onClick={() => setTabIzquierda('favoritos')}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  tabIzquierda === 'favoritos'
                    ? 'border-current'
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
                style={tabIzquierda === 'favoritos' ? { color: theme.primary, borderColor: theme.primary } : {}}
              >
                <Star size={15} />
                Favoritos
                <span
                  className="inline-flex items-center justify-center text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px]"
                  style={{ backgroundColor: theme.primaryLight, color: theme.primaryDark }}
                >
                  {favoritosIds.size}
                </span>
              </button>
            </div>
          </div>

          {tabIzquierda === 'catalogo' ? (
          <>
          {/* Selección de proveedor */}
          <div className="panel-section">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Proveedor
                </label>
                <select
                  name="proveedor"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent bg-white"
                  value={proveedorSeleccionado?.id || ""}
                  onChange={handleProveedorChange}
                >
                  <option value="">— Seleccione un proveedor —</option>
                  {proveedores.map((proveedor) => (
                    <option key={proveedor.id} value={proveedor.id}>
                      {proveedor.nombre}
                      {proveedor.precio_min > 0 && ` (Mín: ${new Intl.NumberFormat("es-ES", {
                        style: "currency", currency: "EUR"
                      }).format(proveedor.precio_min)})`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Badge precio mínimo inline */}
              {proveedorSeleccionado && proveedorSeleccionado.precio_min > 0 && (
                <div className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border mt-5 ${
                  validacionPrecioMinimo.esValido
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-orange-50 text-orange-700 border-orange-200'
                }`}>
                  {validacionPrecioMinimo.esValido ? (
                    <>
                      <CheckCircle size={14} />
                      <span>Mínimo alcanzado</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={14} />
                      <span>
                        Faltan {new Intl.NumberFormat("es-ES", {
                          style: "currency", currency: "EUR", minimumFractionDigits: 2
                        }).format(proveedorSeleccionado.precio_min - totalCombinado)}
                      </span>
                    </>
                  )}
                </div>
              )}

              {/* Badge pendientes de stock sin resolver */}
              {pendientesSinResolver.length > 0 && (
                <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border mt-5 bg-amber-50 text-amber-700 border-amber-200">
                  <AlertTriangle size={14} />
                  <span>{pendientesSinResolver.length} producto(s) pendiente(s) de cantidad</span>
                </div>
              )}
            </div>
          </div>

          {/* Barra de búsqueda y filtros */}
          <div className="panel-section">
            <div className="flex items-center gap-3">
              {mostrarFiltroFamilia && proveedorSeleccionado && (() => {
                const familiaOpciones = getFamiliaFiltroOptions()
                const selectedOption =
                  familiaOpciones.find(o => o.value === familiaFiltro) || familiaOpciones[0]
                const SelectedIcon = selectedOption.Icon
                return (
                  <div ref={familiaFiltroRef} className="relative flex-shrink-0" style={{ minWidth: '180px' }}>
                    <button
                      type="button"
                      onClick={() => setFamiliaFiltroOpen(v => !v)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent transition-colors"
                      aria-haspopup="listbox"
                      aria-expanded={familiaFiltroOpen}
                    >
                      <span className="flex items-center gap-2 text-gray-700">
                        <SelectedIcon size={16} className="text-gray-500" />
                        <span className="font-medium">{selectedOption.label}</span>
                      </span>
                      <ChevronDown
                        size={14}
                        className={`text-gray-400 transform transition-transform ${familiaFiltroOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {familiaFiltroOpen && (
                      <ul
                        role="listbox"
                        className="absolute z-50 mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden py-1"
                      >
                        {familiaOpciones.map(opt => {
                          const ItemIcon = opt.Icon
                          const isActive = opt.value === familiaFiltro
                          return (
                            <li key={opt.value} role="option" aria-selected={isActive}>
                              <button
                                type="button"
                                onClick={() => handleFamiliaFiltroChange(opt.value)}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                                  isActive
                                    ? 'bg-gray-100 text-gray-900 font-medium'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                <ItemIcon size={16} className={isActive ? 'text-gray-700' : 'text-gray-500'} />
                                <span>{opt.label}</span>
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                )
              })()}

              <div className="relative flex-1">
                <input
                  type="text"
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
                  placeholder={proveedorSeleccionado ? "Filtrar por nombre, referencia o marca..." : "Seleccione un proveedor primero"}
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  disabled={!proveedorSeleccionado}
                />
              </div>

              {proveedorSeleccionado && (
                <span className="flex-shrink-0 text-xs text-gray-400 bg-gray-100 px-2.5 py-1.5 rounded-full font-medium">
                  {filteredProducts.length} productos
                </span>
              )}

              {proveedorSeleccionado && favoritosIds.size > 0 && (
                <label
                  className="flex-shrink-0 flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none"
                  title="Mostrar los productos favoritos al principio del catálogo"
                >
                  <input
                    type="checkbox"
                    checked={verFavoritosPrimero}
                    onChange={(e) => setVerFavoritosPrimero(e.target.checked)}
                  />
                  <Star size={13} fill={verFavoritosPrimero ? theme.primary : "none"} style={{ color: theme.primary }} />
                  Favoritos primero
                </label>
              )}
            </div>
          </div>

          {/* Tabla de productos — scroll interno */}
          <div className="productos-scroll-area">
            {!proveedorSeleccionado ? (
              <div className="empty-state">
                <div className="p-4 rounded-full bg-gray-100 mb-4">
                  <AlertTriangle size={28} className="text-yellow-400" />
                </div>
                <p className="font-semibold text-gray-600">Seleccione un proveedor para ver los productos</p>
                <p className="text-sm mt-1 text-gray-400">Use el selector de arriba para comenzar</p>
              </div>
            ) : loadingProducts ? (
              <div className="empty-state">
                <div
                  className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 mb-4"
                  style={{ borderColor: theme.primary }}
                ></div>
                <p className="text-sm text-gray-500">Cargando catálogo de productos...</p>
              </div>
            ) : filteredProducts.length > 0 ? (
              <table className="w-full table-fixed">
                <thead className="productos-thead">
                  <tr>
                    {hayProductosConImagen && (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-14 border-b border-gray-200">
                        Img
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-24 border-b border-gray-200">
                      Ref.
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                      Producto
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-28 border-b border-gray-200">
                      Marca
                    </th>
                    {mostrarSelectorTipo && (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-32 border-b border-gray-200">
                        Tipo
                      </th>
                    )}
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-24 border-b border-gray-200">
                      Precio
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-16 border-b border-gray-200">
                      Emb.
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-24 border-b border-gray-200">
                      Cantidad
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {familias.map((familia) => (
                    <React.Fragment key={familia}>
                      <tr className="bg-gray-50">
                        <td
                          colSpan={6 + (hayProductosConImagen ? 1 : 0) + (mostrarSelectorTipo ? 1 : 0)}
                          className="px-4 py-2 border-y border-gray-200"
                        >
                          <span className="text-xs font-bold uppercase tracking-wider" style={textPrimaryDark}>
                            {familia || "Sin familia"}
                          </span>
                          <span className="ml-2 text-xs font-normal text-gray-400">
                            ({productosPorFamilia[familia].length})
                          </span>
                        </td>
                      </tr>
                      {productosPorFamilia[familia].map((producto) => (
                        <ProductRow
                          key={producto.id}
                          producto={producto}
                          cantidadMostrada={getCantidadMostrada(producto.id)}
                          onCantidadChange={handleCantidadChange}
                          onSolicitarProducto={handleSolicitarProducto}
                          onPrecioChange={handlePrecioChange}
                          onImageClick={handleImageClick}
                          userPermisos={user.permisos}
                          mostrarColumnaImagen={hayProductosConImagen}
                          mostrarColumnaTipo={mostrarSelectorTipo}
                          esFavorito={favoritosIds.has(producto.id)}
                          onToggleFavorito={handleToggleFavoritoProducto}
                        />
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <div className="p-4 rounded-full bg-gray-100 mb-4">
                </div>
                <p className="font-semibold text-gray-500">No se encontraron productos</p>
                <p className="text-sm mt-1 text-gray-400">Prueba con otros términos de búsqueda</p>
              </div>
            )}
          </div>
          </>
          ) : tabIzquierda === 'repetir' ? (
          /* ── CONTENIDO: Repetir Pedido ─────────────────────────────── */
          <>
          {/* Sub-tabs: Pedidos anteriores / Favoritos */}
          <div className="panel-section">
            <div className="flex gap-2">
              <button
                onClick={() => setTabRepetir('anteriores')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  tabRepetir === 'anteriores'
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={tabRepetir === 'anteriores' ? bgPrimary : {}}
              >
                <Clock size={14} />
                Pedidos anteriores
              </button>
              <button
                onClick={() => setTabRepetir('favoritos')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  tabRepetir === 'favoritos'
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={tabRepetir === 'favoritos' ? bgPrimary : {}}
              >
                <Star size={14} />
                Favoritos
              </button>
            </div>
          </div>

          {/* Lista de pedidos */}
          <div className="productos-scroll-area">
            {loadingRepetir ? (
              <div className="empty-state">
                <div
                  className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 mb-4"
                  style={{ borderColor: theme.primary }}
                ></div>
                <p className="text-sm text-gray-500">Cargando pedidos...</p>
              </div>
            ) : (tabRepetir === 'anteriores' ? pedidosAnteriores : pedidosFavoritos).length > 0 ? (
              <div className="divide-y divide-gray-100">
                {(tabRepetir === 'anteriores' ? pedidosAnteriores : pedidosFavoritos).map((pedidoItem) => (
                  <div key={pedidoItem.id} className="bg-white">
                    {/* Fila del pedido (colapsada) */}
                    <div
                      className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => toggleExpandPedido(pedidoItem.id)}
                    >
                      <div className="mr-3 text-gray-400">
                        {expandedPedidos.has(pedidoItem.id) ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-800">
                            Pedido #{pedidoItem.npedido || pedidoItem.id}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(pedidoItem.fecha).toLocaleDateString('es-ES')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-500">{pedidoItem.proveedor_nombre || 'Sin proveedor'}</span>
                          <span className="text-xs text-gray-300">·</span>
                          <span className="text-xs text-gray-500">{pedidoItem.total_articulos} artículos</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Botón favorito */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleToggleFavorito(pedidoItem.id)
                          }}
                          className={`p-1.5 rounded-lg transition-colors ${
                            pedidoItem.favorito === 1
                              ? 'text-yellow-500 hover:bg-yellow-50'
                              : 'text-gray-300 hover:text-yellow-400 hover:bg-gray-100'
                          }`}
                          title={pedidoItem.favorito === 1 ? "Quitar de favoritos" : "Añadir a favoritos"}
                        >
                          <Star size={16} fill={pedidoItem.favorito === 1 ? "currentColor" : "none"} />
                        </button>
                        {/* Botón copiar al pedido */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleCopiarLineas(pedidoItem.id)
                          }}
                          disabled={loadingCopiar}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50"
                          style={bgPrimary}
                          title="Añadir líneas al pedido actual"
                        >
                          <Copy size={12} />
                          Añadir
                        </button>
                      </div>
                    </div>

                    {/* Detalle expandido: líneas del pedido */}
                    {expandedPedidos.has(pedidoItem.id) && pedidoItem.lineas && (
                      <div className="px-4 pb-3">
                        <div className="ml-7 bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Producto</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase w-20">Precio</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase w-16">Cant.</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase w-16">IVA</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {pedidoItem.lineas.map((linea, idx) => (
                                <tr key={`${pedidoItem.id}-${linea.idarticulo}-${idx}`} className="hover:bg-white transition-colors">
                                  <td className="px-3 py-2 text-xs text-gray-700">{linea.nombre}</td>
                                  <td className="px-3 py-2 text-xs text-gray-600 text-right">
                                    {linea.precio != null
                                      ? new Intl.NumberFormat("es-ES", {
                                          style: "currency", currency: "EUR",
                                          minimumFractionDigits: 2, maximumFractionDigits: 3,
                                        }).format(linea.precio)
                                      : '—'}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-600 text-right">{linea.cantidad}</td>
                                  <td className="px-3 py-2 text-xs text-gray-400 text-right">{linea.iva}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="p-4 rounded-full bg-gray-100 mb-4">
                  {tabRepetir === 'favoritos' ? (
                    <Star size={28} className="text-gray-300" />
                  ) : (
                    <RotateCcw size={28} className="text-gray-300" />
                  )}
                </div>
                <p className="font-semibold text-gray-500">
                  {tabRepetir === 'favoritos'
                    ? "No hay pedidos favoritos"
                    : "No hay pedidos anteriores"}
                </p>
                <p className="text-sm mt-1 text-gray-400">
                  {tabRepetir === 'favoritos'
                    ? "Marca pedidos como favoritos desde 'Pedidos anteriores'"
                    : "Los pedidos finalizados aparecerán aquí"}
                </p>
              </div>
            )}
          </div>
          </>
          ) : (
          /* ── CONTENIDO: Favoritos (productos de la clínica) ─────────── */
          <>
          {/* Proveedor (los favoritos se muestran del proveedor elegido) */}
          <div className="panel-section">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Proveedor
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent bg-white"
              value={proveedorSeleccionado?.id || ""}
              onChange={handleProveedorChange}
            >
              <option value="">— Seleccione un proveedor —</option>
              {proveedores.map((proveedor) => (
                <option key={proveedor.id} value={proveedor.id}>{proveedor.nombre}</option>
              ))}
            </select>
          </div>

          <div className="productos-scroll-area">
            {!proveedorSeleccionado ? (
              <div className="empty-state">
                <Star size={28} color="#cbd5e1" />
                <p className="font-semibold text-gray-500 mt-2">Selecciona un proveedor</p>
                <p className="text-sm mt-1 text-gray-400">Aquí verás tus productos favoritos de ese proveedor.</p>
              </div>
            ) : favoritosProductos.length > 0 ? (
              <table className="w-full table-fixed">
                <thead className="productos-thead">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-24 border-b border-gray-200">Ref.</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">Producto</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-28 border-b border-gray-200">Marca</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-24 border-b border-gray-200">Precio</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-16 border-b border-gray-200">Emb.</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-24 border-b border-gray-200">Cantidad</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {favoritosProductos.map((producto) => (
                    <ProductRow
                      key={producto.id}
                      producto={producto}
                      cantidadMostrada={getCantidadMostrada(producto.id)}
                      onCantidadChange={handleCantidadChange}
                      onSolicitarProducto={handleSolicitarProducto}
                      onPrecioChange={handlePrecioChange}
                      onImageClick={handleImageClick}
                      userPermisos={user.permisos}
                      mostrarColumnaImagen={false}
                      mostrarColumnaTipo={false}
                      esFavorito={favoritosIds.has(producto.id)}
                      onToggleFavorito={handleToggleFavoritoProducto}
                    />
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <Star size={28} color="#cbd5e1" />
                <p className="font-semibold text-gray-500 mt-2">No tienes productos favoritos</p>
                <p className="text-sm mt-1 text-gray-400">Pulsa la estrella ☆ junto a un producto (en el catálogo o en el pedido) para añadirlo aquí.</p>
              </div>
            )}
          </div>
          </>
          )}
        </div>

        {/* ── PANEL DERECHO: Detalle del pedido ────────────────────────── */}
        <div className="nuevo-pedido-right">

          {/* Header del detalle */}
          <div className="panel-section-colored text-white" style={bgPrimary}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-bold text-base leading-tight">Detalle del Pedido</h2>
                <p className="text-xs opacity-75 mt-0.5">#{pedidoId}</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setDetalleVisible(!detalleVisible)}
                  className="p-1.5 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                  title={detalleVisible ? "Ocultar líneas" : "Mostrar líneas"}
                >
                  <Eye size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Líneas del pedido — scroll interno, partido en dos secciones */}
          <div className="detalle-scroll-area">
            {detalleVisible ? (
              lineasCombinadas.length > 0 ? (
                (() => {
                  const lineasReales = lineasCombinadas.filter(l => !l.pendienteStock)
                  const lineasPendientes = lineasCombinadas.filter(l => l.pendienteStock)
                  return (
                    <>
                      {lineasReales.length > 0 && (
                        <div>
                          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                            Pedido ({lineasReales.length})
                          </div>
                          <table className="w-full">
                            <thead className="detalle-thead">
                              <tr className="border-b border-gray-200">
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Producto</th>
                                <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">Precio</th>
                                <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">Cant.</th>
                                <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">Subt.</th>
                                <th className="px-2 py-2.5 text-center w-8"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {lineasReales.map((linea) => (
                                <PedidoLineRow
                                  key={linea.id}
                                  linea={linea}
                                  onCantidadChange={handleCantidadChange}
                                  onEliminarLinea={handleEliminarLinea}
                                  esFavorito={favoritosIds.has(linea.idarticulo)}
                                  onToggleFavorito={handleToggleFavoritoProducto}
                                />
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {lineasPendientes.length > 0 && (
                        <div>
                          <div
                            className="px-4 py-2 border-y text-[11px] font-bold uppercase tracking-wider flex items-center gap-1"
                            style={{ backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fde68a' }}
                          >
                            <AlertTriangle size={12} />
                            Pendientes por stock bajo ({lineasPendientes.length})
                          </div>
                          <table className="w-full" style={{ backgroundColor: '#fffbeb' }}>
                            <tbody className="divide-y divide-amber-100">
                              {lineasPendientes.map((linea) => (
                                <PedidoLineRow
                                  key={linea.id}
                                  linea={linea}
                                  onCantidadChange={handleCantidadChange}
                                  onEliminarLinea={handleEliminarLinea}
                                  esFavorito={favoritosIds.has(linea.idarticulo)}
                                  onToggleFavorito={handleToggleFavoritoProducto}
                                />
                              ))}
                            </tbody>
                          </table>
                          <div className="px-4 py-2 text-[10px] text-amber-700 bg-amber-50 border-b border-amber-100">
                            Introduce la cantidad o elimina la línea para finalizar el pedido.
                          </div>
                        </div>
                      )}
                    </>
                  )
                })()
              ) : (
                <div className="empty-state">
                  <div className="p-4 rounded-full bg-gray-100 mb-3">
                    <Package size={28} className="text-gray-300" />
                  </div>
                  <p className="font-semibold text-gray-500 text-center text-sm">Sin productos aún</p>
                  <p className="text-xs text-gray-400 mt-1 text-center">Introduce cantidades en el catálogo de la izquierda</p>
                </div>
              )
            ) : (
              lineasCombinadas.length > 0 && (
                <div className="p-4">
                  <div className="flex justify-between items-center py-2.5 px-3 bg-gray-50 rounded-lg text-sm">
                    <span className="text-gray-500">Productos en pedido</span>
                    <span className="font-semibold text-gray-800">{lineasCombinadas.length}</span>
                  </div>
                </div>
              )
            )}
          </div>

          {/* Footer del pedido: Total + Botones */}
          <div className="detalle-footer">
            {/* Total */}
            {lineasCombinadas.length > 0 && (
              <div
                className="flex justify-between items-center px-4 py-3 rounded-lg mb-3"
                style={bgPrimaryLight}
              >
                <span className="text-sm font-semibold" style={textPrimaryDark}>Total (IVA incluido)</span>
                <span className="text-base font-bold" style={textPrimaryDark}>
                  {new Intl.NumberFormat("es-ES", {
                    style: "currency",
                    currency: "EUR",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(totalCombinado)}
                </span>
              </div>
            )}

            {/* Botones de acción */}
            <div className="grid grid-cols-2 gap-3">
              {/* Botón Comentarios */}
              <button
                onClick={() => setComentariosModalVisible(true)}
                className="px-3 py-2.5 rounded-lg text-white text-sm font-medium flex items-center justify-center gap-2 transition-colors hover:opacity-90"
                style={bgPrimary}
              >
                <MessageCircle size={15} />
                <span>Comentarios</span>
                {comentarios.length > 0 && (
                  <span
                    className="rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}
                  >
                    {comentarios.length}
                  </span>
                )}
              </button>

              {/* Botón Finalizar / Enviar para Aprobación */}
              <button
                onClick={handleOpenModal}
                disabled={botonFinalizarDesactivado}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  botonFinalizarDesactivado
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                    : "text-white hover:opacity-90"
                }`}
                style={botonFinalizarDesactivado ? {} : bgPrimary}
                title={!validacionPrecioMinimo.esValido ? validacionPrecioMinimo.mensaje : ""}
              >
                {necesitaAprobacion ? (
                  <>
                    <UserCheck size={15} />
                    <span>Enviar</span>
                  </>
                ) : (
                  <>
                    <CheckCircle size={15} />
                    <span>Finalizar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Modal de imagen en grande ─────────────────────────────────── */}
      <ImageModal
        isOpen={modalImagenVisible}
        imageSrc={imagenSeleccionada.src}
        imageAlt={imagenSeleccionada.alt}
        onClose={handleCloseImageModal}
      />

      {/* ── Modal: Finalizar pedido ───────────────────────────────────── */}
      {modalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-fadeIn"
            ref={modalRef}
          >
            {/* Header modal */}
            <div className="text-white px-6 py-4" style={bgPrimary}>
              <h3 className="text-lg font-bold">
                {necesitaAprobacion ? "Enviar Pedido para Aprobación" : "Finalizar Pedido"}
              </h3>
              <p className="text-xs opacity-75 mt-0.5">Pedido #{pedidoId} · {proveedorSeleccionado?.nombre}</p>
            </div>

            <form onSubmit={handleFinalizarPedido} className="p-6">
              {necesitaAprobacion && (
                <div className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start text-blue-800 text-sm">
                    <Clock size={15} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span>Este pedido será enviado a la clínica matriz para su aprobación antes de ser procesado.</span>
                  </div>
                </div>
              )}

              {/* Selector de tipo de pedido
                  Solo se muestra cuando el cliente lo exige (131/999999) Y el tipo
                  NO se puede deducir solo: es decir, hay sanitario genérico sin subfamilia. */}
              {mostrarSelectorTipo && !tipoPedidoEsAutoderivado && (
                <div className="mb-5">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Tipo de pedido <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {/* Cirugía Plástica */}
                    <button
                      type="button"
                      onClick={() => {
                        console.log('🔘 Seleccionado: Cirugía Plástica')
                        setTipoPedido('cirugia_plastica')
                      }}
                      className={`p-4 border-2 rounded-lg transition-all text-left ${
                        tipoPedido === 'cirugia_plastica'
                          ? 'shadow-sm'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      style={tipoPedido === 'cirugia_plastica'
                        ? { borderColor: theme.primary, backgroundColor: theme.primaryLight }
                        : {}}
                    >
                      <Scissors
                        className="w-7 h-7 mb-2"
                        style={{ color: tipoPedido === 'cirugia_plastica' ? theme.primary : '#9ca3af' }}
                      />
                      <span
                        className="text-sm font-medium block"
                        style={{ color: tipoPedido === 'cirugia_plastica' ? theme.primaryDark : '#374151' }}
                      >
                        Cirugía Plástica
                      </span>
                    </button>

                    {/* Medicina Estética */}
                    {/* Bloqueada si el pedido es de Oficina/Limpieza: en ese caso el tipo es SÍ o SÍ Cirugía */}
                    <button
                      type="button"
                      disabled={categoriaPedidoActual === 'oficina_limpieza'}
                      onClick={() => {
                        if (categoriaPedidoActual === 'oficina_limpieza') return
                        console.log('🔘 Seleccionado: Medicina Estética')
                        setTipoPedido('medicina_estetica')
                      }}
                      className={`p-4 border-2 rounded-lg transition-all text-left ${
                        categoriaPedidoActual === 'oficina_limpieza'
                          ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                          : tipoPedido === 'medicina_estetica'
                            ? 'border-purple-500 bg-purple-50 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Sparkles
                        className={`w-7 h-7 mb-2 ${
                          tipoPedido === 'medicina_estetica' && categoriaPedidoActual !== 'oficina_limpieza' ? 'text-purple-600' : 'text-gray-400'
                        }`}
                      />
                      <span
                        className={`text-sm font-medium block ${
                          tipoPedido === 'medicina_estetica' && categoriaPedidoActual !== 'oficina_limpieza' ? 'text-purple-700' : 'text-gray-700'
                        }`}
                      >
                        Medicina Estética
                      </span>
                    </button>
                  </div>
                  {categoriaPedidoActual === 'oficina_limpieza' && (
                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                      <AlertTriangle size={12} />
                      Los pedidos de Oficina y Limpieza son siempre de tipo Cirugía Plástica.
                    </p>
                  )}
                  {mostrarSelectorTipo && !tipoPedido && categoriaPedidoActual !== 'oficina_limpieza' && (
                    <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                      <AlertTriangle size={12} />
                      Selecciona un tipo de pedido para continuar
                    </p>
                  )}
                </div>
              )}

              {/* Número de pedido del cliente */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nº de pedido del cliente <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                  type="text"
                  value={npedidoCliente}
                  onChange={(e) => setNpedidoCliente(e.target.value)}
                  placeholder="Número de pedido del cliente..."
                />
              </div>

              {/* Resumen */}
              <div className="bg-gray-50 rounded-lg p-4 mb-5 border border-gray-100">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Resumen</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Proveedor</span>
                    <span className="font-medium text-gray-800">{proveedorSeleccionado?.nombre}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Productos</span>
                    <span className="font-medium text-gray-800">{lineasCombinadas.length}</span>
                  </div>
                  {mostrarSelectorTipo && tipoPedido && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Tipo</span>
                      <span className="font-medium text-gray-800">
                        {tipoPedido === 'cirugia_plastica' ? 'Cirugía Plástica' : 'Medicina Estética'}
                      </span>
                    </div>
                  )}
                  {necesitaAprobacion && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Estado</span>
                      <span className="font-medium text-yellow-600">Pendiente de aprobación</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 mt-1 border-t border-gray-200">
                    <span className="font-semibold text-gray-800">Total</span>
                    <span className="font-bold text-base" style={textPrimaryDark}>
                      {new Intl.NumberFormat("es-ES", {
                        style: "currency",
                        currency: "EUR",
                      }).format(totalCombinado)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Botones del modal */}
              <div className="flex gap-3">
                <button
                  type="button"
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                  onClick={() => setModalVisible(false)}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={mostrarSelectorTipo && !tipoPedidoEsAutoderivado && !tipoPedido}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                    mostrarSelectorTipo && !tipoPedidoEsAutoderivado && !tipoPedido
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'text-white hover:opacity-90'
                  }`}
                  style={(mostrarSelectorTipo && !tipoPedido) ? {} : bgPrimary}
                >
                  {necesitaAprobacion ? (
                    <>
                      <UserCheck size={16} />
                      Enviar para Aprobación
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Confirmar Pedido
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Comentarios ────────────────────────────────────────── */}
      {comentariosModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-fadeIn"
            ref={comentariosModalRef}
          >
            {/* Header modal comentarios */}
            <div className="text-white px-6 py-4" style={bgPrimary}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">Comentarios del Pedido</h3>
                  <p className="text-xs opacity-75 mt-0.5">Añade notas o solicitudes especiales</p>
                </div>
                <button
                  onClick={() => setComentariosModalVisible(false)}
                  className="p-1.5 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="p-5">
              <form onSubmit={handleAddComentario} className="mb-4">
                <div className="mb-3">
                  <input
                    id="ccc"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                    type="text"
                    placeholder="Escribe tu comentario o solicitud..."
                    value={nuevoComentario}
                    onChange={(e) => setNuevoComentario(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                    type="text"
                    placeholder="Cantidad (opcional)"
                    value={cantidadComentario}
                    onChange={(e) => setCantidadComentario(e.target.value)}
                  />
                  <input
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                    type="text"
                    placeholder="Precio (opcional)"
                    value={precioComentario}
                    onChange={(e) => setPrecioComentario(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  className={`w-full py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                    !nuevoComentario.trim() ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'text-white hover:opacity-90'
                  }`}
                  style={nuevoComentario.trim() ? bgPrimary : {}}
                  disabled={!nuevoComentario.trim()}
                >
                  <Plus size={16} />
                  Añadir Comentario
                </button>
              </form>

              {comentarios.length > 0 && (
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    {comentarios.length} comentario{comentarios.length !== 1 ? 's' : ''}
                  </p>
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {comentarios.map((comentario) => (
                      <div
                        key={comentario.id}
                        className="flex items-start justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                      >
                        <div className="flex-1 pr-3 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{comentario.comentario}</p>
                          {(comentario.cantidad > 0 || comentario.precio > 0) && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {comentario.cantidad > 0 && `Cant: ${comentario.cantidad}`}
                              {comentario.cantidad > 0 && comentario.precio > 0 && " · "}
                              {comentario.precio > 0 && `Precio: ${new Intl.NumberFormat("es-ES", {
                                style: "currency", currency: "EUR",
                              }).format(comentario.precio)}`}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteComentario(comentario.id)}
                          className="text-gray-300 hover:text-red-500 p-1 rounded flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <X size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default NuevoPedido